/**
 * Definições principal — single-source-of-truth admin page for the public
 * homepage. Replaces the scattered HomeButtons / Footer / Settings / Intro
 * pages. All sections collapse into expandable cards; every text field is
 * available in PT/EN/ES.
 *
 * Wiring: PUT /api/homepage-config (admin) returns the canonical config
 * back; GET /api/homepage-config (public) is what the public Home.jsx
 * renders.
 */
import React, { useEffect, useRef, useState } from "react";
import {
  Settings2, Save, RotateCcw, ChevronDown, ChevronRight,
  Image as ImageIcon, Menu, Sparkles, Layers, Gamepad2,
  BarChart3, Trash2, Plus, Upload, X, Eye, CalendarClock, Mail,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { ChampionBadgePanel } from "./components/ChampionBadgePanel.jsx";
import { setPath } from "./components/setPath.js";
import { LANGS, BADGE_ICONS, Section, ImageUploader, MultiLangText } from "./components/shared.jsx";
import { SelectField, RangeField, ColorField } from "./components/fields.jsx";
import { HeroImageUploader, HeroImageControls } from "./components/HeroImagePanel.jsx";
import { TabletVideoPanel } from "./components/TabletVideoPanel.jsx";
import { SideVideoPanel } from "./components/SideVideoPanel.jsx";
import { LearnLinkButtonPanel } from "./components/LearnLinkButtonPanel.jsx";
import { FloatingPreview } from "./components/FloatingPreview.jsx";
import { SectionLayoutControls } from "./components/LayoutPanels.jsx";
import { SiteThemePanel, LogoRingPanel, CtaLayoutPanel } from "./components/ThemePanels.jsx";
import { AITitleGenerator } from "./components/AITitleGenerator.jsx";
import { CtaAdvancedOptions } from "./components/CtaAdvancedOptions.jsx";
import { GameplayBrandPanel } from "./components/GameplayBrandPanel.jsx";
import { GameplayPillsPanel } from "./components/GameplayPillsPanel.jsx";
import { SmtpPanel } from "./components/SmtpPanel.jsx";
import { ExportImportButtons } from "./components/ExportImportButtons.jsx";
import { invalidateHomepageConfig } from "@/lib/homepageConfigCache";

export default function MainSettings() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  // Accordion behaviour — only one section open at a time. Opening a section
  // auto-closes any other section that was open. Clicking the currently open
  // section closes it (leaving all closed). `theme` starts open by default.
  const [open, setOpen] = useState({ theme: true, logo: false, gameplay_brand: false, gameplay_pills: false, nav: false, header_actions: false, hero: false, ctas: false, phases: false, deadline: false, online: false, online_secondary: false, footer: false, smtp: false });

  const toggleOpen = (k) => setOpen((s) => {
    const wasOpen = !!s[k];
    // Build a fresh object with every key set to false, then toggle only `k`.
    const next = Object.fromEntries(Object.keys(s).map((key) => [key, false]));
    next[k] = !wasOpen;
    return next;
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/homepage-config");
      setCfg(data);
    } catch (err) {
      toast.error("Erro a carregar configuração da homepage");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Ctrl+S / Cmd+S → guardar sem ter de procurar o botão.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cfg, saving]);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const { _id, ...payload } = cfg;
      const { data } = await api.put("/homepage-config", payload);
      setCfg(data);
      setSavedAt(Date.now());
      invalidateHomepageConfig();
      toast.success("Definições da homepage guardadas");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro a guardar");
    } finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Repor TODOS os defaults da homepage? Esta ação não pode ser desfeita.")) return;
    try {
      const { data } = await api.post("/homepage-config/reset");
      setCfg(data);
      toast.success("Defaults repostos");
    } catch { toast.error("Erro a repor"); }
  };

  if (loading || !cfg) return <div className="text-slate-500">A carregar…</div>;

  // ---------- HELPERS for nested edits ----------
  const updateHero = (lang, field, value) => setCfg(setPath(cfg, ["hero", `${field}_${lang}`], value));
  const updateLogoUrl = (v) => setCfg({ ...cfg, logo_url: v });
  const updateNavItem = (idx, field, value) => setCfg(setPath(cfg, ["nav_items", idx, field], value));
  const addNavItem = () => setCfg({
    ...cfg,
    nav_items: [...cfg.nav_items, { id: `nav-${Date.now()}`, label_pt: "", label_en: "", label_es: "", url: "/" }],
  });
  const removeNavItem = (idx) => setCfg({ ...cfg, nav_items: cfg.nav_items.filter((_, i) => i !== idx) });

  const updateCta = (list, idx, field, value) => setCfg(setPath(cfg, [list, idx, field], value));

  const updatePhase = (idx, field, value) => setCfg(setPath(cfg, ["phases_section", "phases", idx, field], value));
  const updatePhaseSectionTitle = (lang, value) => setCfg(setPath(cfg, ["phases_section", `title_${lang}`], value));

  const updateOnlineCard = (idx, field, value) => setCfg(setPath(cfg, ["online_section", "cards", idx, field], value));
  const updateOnlineSectionTitle = (lang, value) => setCfg(setPath(cfg, ["online_section", `title_${lang}`], value));

  const updateFooterStatItem = (idx, field, value) => setCfg(setPath(cfg, ["footer_stats", "items", idx, field], value));
  const updateFooterTagline = (lang, value) => setCfg(setPath(cfg, ["footer_stats", `tagline_${lang}`], value));
  const updateFooterTaglineHighlight = (lang, value) => setCfg(setPath(cfg, ["footer_stats", `tagline_highlight_${lang}`], value));
  const updateSocial = (key, value) => setCfg(setPath(cfg, ["footer_stats", "social_links", key], value));

  return (
    <div className="space-y-4 max-w-5xl pb-24" data-testid="main-settings-page">
      <header className="flex items-center justify-between sticky top-0 z-30 bg-slate-50/95 backdrop-blur-sm py-3 -my-3 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <Settings2 className="w-7 h-7 text-sky-600" />
          <div>
            <h1 className="text-2xl font-[Fredoka] font-bold text-slate-900">Definições principal</h1>
            <p className="text-xs text-slate-500">Tudo o que aparece na homepage pública — cada texto em PT/EN/ES.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <ExportImportButtons
            exportUrl="/homepage-config/export"
            importUrl="/homepage-config/import"
            filePrefix="homepage-config"
            onImported={() => window.location.reload()}
            testidPrefix="homepage"
          />
          <Button variant="outline" onClick={resetDefaults} className="rounded-full" data-testid="reset-defaults-btn">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Repor defaults
          </Button>
          <Button onClick={save} disabled={saving} className="bg-sky-500 hover:bg-sky-600 rounded-full" data-testid="save-btn">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "A guardar…" : "Guardar tudo"}
          </Button>
        </div>
      </header>

      {/* FLOATING SAVE BUTTON — sempre visível em qualquer ponto da página */}
      <button
        onClick={save}
        disabled={saving}
        className="fixed bottom-6 right-6 z-50 bg-sky-500 hover:bg-sky-600 disabled:opacity-70 text-white font-[Fredoka] font-bold rounded-full shadow-2xl px-6 py-3 flex items-center gap-2 transition hover:scale-105 active:scale-95"
        style={{ boxShadow: "0 12px 32px -6px rgba(14,165,233,0.55), 0 6px 14px -4px rgba(0,0,0,0.3)" }}
        title="Guardar tudo (Ctrl+S)"
        data-testid="floating-save-btn"
      >
        <Save className="w-5 h-5" />
        <span>{saving ? "A guardar…" : "Guardar tudo"}</span>
      </button>

      {/* FLOATING LIVE PREVIEW — iframe da Homepage, auto-refresh após save */}
      <FloatingPreview cfg={cfg} savedAt={savedAt} onSave={save} saving={saving} />

      {/* SITE THEME — global background image + colors + spotlight */}
      <Section id="theme" icon={Sparkles} title="Tema do site (fundo, cores, spotlight)" open={open.theme} onToggle={() => toggleOpen("theme")}>
        <SiteThemePanel cfg={cfg} setCfg={setCfg} />

        {/* ── Seta "scroll down" a piscar (canto inferior do ecrã) ── */}
        <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50/70 p-3 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs font-bold uppercase text-slate-600">Seta &ldquo;scroll para baixo&rdquo; a piscar</div>
            <label className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none">
              <input
                type="checkbox"
                checked={cfg.scroll_hint?.enabled !== false}
                onChange={(e) => setCfg(setPath(cfg, ["scroll_hint", "enabled"], e.target.checked))}
                className="accent-emerald-600"
                data-testid="scroll-hint-enabled"
              />
              <span className={`font-bold ${cfg.scroll_hint?.enabled === false ? "text-slate-400" : "text-emerald-700"}`}>
                {cfg.scroll_hint?.enabled === false ? "Não activo" : "Activo"}
              </span>
            </label>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <RangeField
              label="Distância ao fundo do ecrã"
              value={cfg.scroll_hint?.bottom_offset ?? 24}
              min={0} max={120} unit="px"
              onChange={(v) => setCfg(setPath(cfg, ["scroll_hint", "bottom_offset"], v))}
              testid="scroll-hint-bottom"
            />
            <RangeField
              label="Tamanho do círculo"
              value={cfg.scroll_hint?.size ?? 54}
              min={32} max={100} unit="px"
              onChange={(v) => setCfg(setPath(cfg, ["scroll_hint", "size"], v))}
              testid="scroll-hint-size"
            />
            <RangeField
              label="Esconder após scroll de"
              value={cfg.scroll_hint?.hide_after_scroll ?? 120}
              min={20} max={600} unit="px"
              onChange={(v) => setCfg(setPath(cfg, ["scroll_hint", "hide_after_scroll"], v))}
              testid="scroll-hint-hide-after"
            />
            <div>
              <Label className="text-xs text-slate-600">Velocidade da animação ({(cfg.scroll_hint?.bounce_speed ?? 1.6).toFixed(1)}s)</Label>
              <input
                type="range" min={0.5} max={4} step={0.1}
                value={cfg.scroll_hint?.bounce_speed ?? 1.6}
                onChange={(e) => setCfg(setPath(cfg, ["scroll_hint", "bounce_speed"], Number(e.target.value)))}
                className="w-full mt-2 accent-teal-600"
                data-testid="scroll-hint-speed"
              />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <ColorField
              label="Cor da seta + rebordo + glow"
              value={cfg.scroll_hint?.icon_color || "#3DA5FF"}
              onChange={(v) => setCfg(setPath(cfg, ["scroll_hint", "icon_color"], v))}
              testid="scroll-hint-icon-color"
            />
            <ColorField
              label="Cor de fundo do círculo"
              value={cfg.scroll_hint?.bg_color || "#0F1A55"}
              onChange={(v) => setCfg(setPath(cfg, ["scroll_hint", "bg_color"], v))}
              testid="scroll-hint-bg-color"
            />
          </div>
          <div className="text-[11px] text-slate-500">A seta desaparece automaticamente quando o utilizador faz scroll além do valor acima ou chega ao fim da página. Ao clicar, empurra a página ~85% da altura do ecrã.</div>
        </div>
      </Section>

      {/* LOGO */}
      <Section id="logo" icon={ImageIcon} title="Logo do site" open={open.logo} onToggle={() => toggleOpen("logo")}>
        <ImageUploader
          label="Logo (URL ou ficheiro local)"
          value={cfg.logo_url}
          onChange={updateLogoUrl}
          testid="logo-url"
          previewClass="h-16 mt-2 object-contain bg-slate-50 rounded p-2"
        />

        {/* Brand name — bold text next to the logo image, per language */}
        <div className="mt-5 space-y-1">
          <Label className="text-xs font-bold text-slate-700">Nome da marca (ao lado do logo)</Label>
          <p className="text-[11px] text-slate-500">Texto bold grande — ex: <code className="font-mono">SUPERTMATIK</code></p>
          <div className="grid sm:grid-cols-3 gap-2">
            {LANGS.map(({ code, flag }) => (
              <Input
                key={code}
                value={cfg.logo_name?.[code] || ""}
                onChange={(e) => setCfg(setPath(cfg, ["logo_name", code], e.target.value))}
                placeholder={`${flag} Nome`}
                data-testid={`logo-name-${code}`}
              />
            ))}
          </div>
        </div>

        {/* Brand tagline — small uppercase subtitle below the brand name */}
        <div className="mt-4 space-y-1">
          <Label className="text-xs font-bold text-slate-700">Subtítulo / tagline (por baixo do nome)</Label>
          <p className="text-[11px] text-slate-500">Texto pequeno em CAIXA ALTA — ex: <code className="font-mono">Campeonatos escolares</code></p>
          <div className="grid sm:grid-cols-3 gap-2">
            {LANGS.map(({ code, flag }) => (
              <Input
                key={code}
                value={cfg.logo_tagline?.[code] || ""}
                onChange={(e) => setCfg(setPath(cfg, ["logo_tagline", code], e.target.value))}
                placeholder={`${flag} Subtítulo`}
                data-testid={`logo-tagline-${code}`}
              />
            ))}
          </div>
        </div>

        {/* Style — Brand name (SUPERTMATIK) */}
        <div className="mt-5 rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-wide text-indigo-800">🎨 Formato · Nome da marca</div>
          <div className="grid sm:grid-cols-2 gap-2">
            <div className="flex items-center gap-2">
              <Label className="text-[10px] w-14">Cor</Label>
              <Input type="color" value={cfg.logo_name_style?.color || "#FFFFFF"} onChange={(e) => setCfg(setPath(cfg, ["logo_name_style", "color"], e.target.value))} className="h-8 w-14 p-1" data-testid="logo-name-color" />
              <Input value={cfg.logo_name_style?.color || "#FFFFFF"} onChange={(e) => setCfg(setPath(cfg, ["logo_name_style", "color"], e.target.value))} placeholder="#FFFFFF" className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-[10px]">Tamanho ({cfg.logo_name_style?.size ?? 24}px)</Label>
              <Input type="range" min={10} max={64} value={cfg.logo_name_style?.size ?? 24} onChange={(e) => setCfg(setPath(cfg, ["logo_name_style", "size"], Number(e.target.value)))} data-testid="logo-name-size" />
            </div>
            <div>
              <Label className="text-[10px]">Peso ({cfg.logo_name_style?.weight ?? 900})</Label>
              <Input type="range" min={100} max={900} step={100} value={cfg.logo_name_style?.weight ?? 900} onChange={(e) => setCfg(setPath(cfg, ["logo_name_style", "weight"], Number(e.target.value)))} data-testid="logo-name-weight" />
            </div>
            <div>
              <Label className="text-[10px]">Espaçamento letras ({cfg.logo_name_style?.letter_spacing ?? -0.5}px)</Label>
              <Input type="range" min={-2} max={10} step={0.5} value={cfg.logo_name_style?.letter_spacing ?? -0.5} onChange={(e) => setCfg(setPath(cfg, ["logo_name_style", "letter_spacing"], Number(e.target.value)))} data-testid="logo-name-tracking" />
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-xs">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!cfg.logo_name_style?.uppercase} onChange={(e) => setCfg(setPath(cfg, ["logo_name_style", "uppercase"], e.target.checked))} data-testid="logo-name-uppercase" />
              <span>Maiúsculas</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={cfg.logo_name_style?.shadow ?? true} onChange={(e) => setCfg(setPath(cfg, ["logo_name_style", "shadow"], e.target.checked))} data-testid="logo-name-shadow" />
              <span>Sombra/brilho</span>
            </label>
          </div>
        </div>

        {/* Style — Tagline (BY @EUDACTICA) */}
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-wide text-amber-800">🎨 Formato · Subtítulo / tagline</div>
          <div className="grid sm:grid-cols-2 gap-2">
            <div className="flex items-center gap-2">
              <Label className="text-[10px] w-14">Cor</Label>
              <Input type="color" value={cfg.logo_tagline_style?.color || "#FFD93D"} onChange={(e) => setCfg(setPath(cfg, ["logo_tagline_style", "color"], e.target.value))} className="h-8 w-14 p-1" data-testid="logo-tagline-color" />
              <Input value={cfg.logo_tagline_style?.color || "#FFD93D"} onChange={(e) => setCfg(setPath(cfg, ["logo_tagline_style", "color"], e.target.value))} placeholder="#FFD93D" className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-[10px]">Tamanho ({cfg.logo_tagline_style?.size ?? 10}px)</Label>
              <Input type="range" min={6} max={32} value={cfg.logo_tagline_style?.size ?? 10} onChange={(e) => setCfg(setPath(cfg, ["logo_tagline_style", "size"], Number(e.target.value)))} data-testid="logo-tagline-size" />
            </div>
            <div>
              <Label className="text-[10px]">Peso ({cfg.logo_tagline_style?.weight ?? 700})</Label>
              <Input type="range" min={100} max={900} step={100} value={cfg.logo_tagline_style?.weight ?? 700} onChange={(e) => setCfg(setPath(cfg, ["logo_tagline_style", "weight"], Number(e.target.value)))} data-testid="logo-tagline-weight" />
            </div>
            <div>
              <Label className="text-[10px]">Espaçamento letras ({cfg.logo_tagline_style?.letter_spacing ?? 2.5}px)</Label>
              <Input type="range" min={-2} max={10} step={0.5} value={cfg.logo_tagline_style?.letter_spacing ?? 2.5} onChange={(e) => setCfg(setPath(cfg, ["logo_tagline_style", "letter_spacing"], Number(e.target.value)))} data-testid="logo-tagline-tracking" />
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-xs">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={cfg.logo_tagline_style?.uppercase ?? true} onChange={(e) => setCfg(setPath(cfg, ["logo_tagline_style", "uppercase"], e.target.checked))} data-testid="logo-tagline-uppercase" />
              <span>Maiúsculas</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={cfg.logo_tagline_style?.shadow ?? true} onChange={(e) => setCfg(setPath(cfg, ["logo_tagline_style", "shadow"], e.target.checked))} data-testid="logo-tagline-shadow" />
              <span>Sombra/brilho</span>
            </label>
          </div>
        </div>

        {/* Decorative ring around the (circular) logo */}
        <LogoRingPanel cfg={cfg} setCfg={setCfg} />

        {/* ── Links (internos/externos) de cada elemento do cabeçalho ── */}
        <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3 space-y-2 mt-4">
          <div className="text-xs font-bold uppercase text-slate-600">Links independentes dos elementos do cabeçalho</div>
          <div className="text-[11px] text-slate-500">Cada elemento (imagem, texto principal e subtítulo) tem o seu próprio link. Escreve URL interna (ex.: <code>/rankings</code>), externa (<code>https://...</code>), email (<code>mailto:…</code>) ou telefone (<code>tel:…</code>). Deixa em branco para desactivar o clique.</div>
          <div>
            <Label className="text-[10px] text-slate-500">Imagem / logótipo (círculo)</Label>
            <Input
              value={cfg.header_links?.logo_url ?? ""}
              onChange={(e) => setCfg(setPath(cfg, ["header_links", "logo_url"], e.target.value))}
              placeholder="/ · /home · https://outrosite.com · mailto:info@..."
              data-testid="header-link-logo"
            />
          </div>
          <div>
            <Label className="text-[10px] text-slate-500">
              Texto principal{(cfg.logo_name?.pt || cfg.logo_name?.en) ? ` — "${cfg.logo_name?.pt || cfg.logo_name?.en}"` : ""}
            </Label>
            <Input
              value={cfg.header_links?.brand_name_url ?? ""}
              onChange={(e) => setCfg(setPath(cfg, ["header_links", "brand_name_url"], e.target.value))}
              placeholder="/ · https://supertmatik.net"
              data-testid="header-link-brand-name"
            />
          </div>
          <div>
            <Label className="text-[10px] text-slate-500">
              Subtítulo{(cfg.logo_tagline?.pt || cfg.logo_tagline?.en) ? ` — "${cfg.logo_tagline?.pt || cfg.logo_tagline?.en}"` : ""}
            </Label>
            <Input
              value={cfg.header_links?.brand_tagline_url ?? ""}
              onChange={(e) => setCfg(setPath(cfg, ["header_links", "brand_tagline_url"], e.target.value))}
              placeholder="/ · https://eudactica.com"
              data-testid="header-link-brand-tagline"
            />
          </div>
        </div>
      </Section>

      {/* GAMEPLAY BRAND — logo/texto nos ecrãs de jogo (VS Tempo/Robot/Humano) */}
      <Section id="gameplay_brand" icon={Gamepad2} title="Logo no jogo (VS Tempo · VS Robot · VS Humano)" open={open.gameplay_brand} onToggle={() => toggleOpen("gameplay_brand")}>
        <GameplayBrandPanel cfg={cfg} setCfg={setCfg} />
      </Section>

      {/* GAMEPLAY PILLS — caixas do cronómetro + nível/desafio */}
      <Section id="gameplay_pills" icon={Gamepad2} title="Caixas de Cronómetro e Nível (em todos os modos de jogo)" open={open.gameplay_pills} onToggle={() => toggleOpen("gameplay_pills")}>
        <GameplayPillsPanel cfg={cfg} setCfg={setCfg} />
      </Section>

      {/* HEADER ACTIONS — 4 botões redondos + pill de idioma + botão SAIR */}
      <Section id="header_actions" icon={Menu} title="Cabeçalho — Botões (Email · WhatsApp · Início · Ranking) + pill + PT/EN/ES + SAIR" open={open.header_actions} onToggle={() => toggleOpen("header_actions")}>
        {(() => {
          const pill = cfg.jogar_online_button || {};
          const on = pill.enabled !== false;
          return (
            <div className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 ${on ? "bg-lime-50 border-lime-300" : "bg-slate-50 border-slate-200"}`} data-testid="header-pill-toggle-box">
              <div className="text-sm">
                <div className="font-bold text-slate-800">
                  Botão pill &quot;{pill.label_pt || "TREINOS LIVRES"}&quot; <span className="font-normal text-slate-500">(à direita, com menu)</span>
                </div>
                <div className="text-xs text-slate-500">{on ? "Visível no cabeçalho." : "Escondido do cabeçalho."} Textos, link e submenu editam-se em &quot;Menu de navegação&quot;.</div>
              </div>
              <Switch checked={on} onCheckedChange={(v) => setCfg(setPath(cfg, ["jogar_online_button", "enabled"], !!v))} data-testid="header-pill-enabled" />
            </div>
          );
        })()}
        <div className="text-xs font-bold uppercase text-slate-500">Botões redondos do cabeçalho</div>
        <div className="space-y-3">
          {(cfg.header_actions?.buttons || []).map((btn, idx) => (
            <div key={btn.id || idx} className="rounded-lg border border-slate-200 p-3 space-y-2 bg-slate-50/40">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="inline-flex rounded border border-slate-300 bg-white overflow-hidden">
                    <button
                      type="button"
                      disabled={idx === 0}
                      onClick={() => {
                        const arr = [...(cfg.header_actions?.buttons || [])];
                        [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
                        setCfg(setPath(cfg, ["header_actions", "buttons"], arr));
                      }}
                      className="px-1.5 py-0.5 text-xs hover:bg-slate-100 disabled:opacity-30"
                      title="Mover para a esquerda (antes)"
                      data-testid={`hdr-btn-${idx}-up`}
                    >◀</button>
                    <button
                      type="button"
                      disabled={idx === (cfg.header_actions?.buttons || []).length - 1}
                      onClick={() => {
                        const arr = [...(cfg.header_actions?.buttons || [])];
                        [arr[idx + 1], arr[idx]] = [arr[idx], arr[idx + 1]];
                        setCfg(setPath(cfg, ["header_actions", "buttons"], arr));
                      }}
                      className="px-1.5 py-0.5 text-xs hover:bg-slate-100 disabled:opacity-30 border-l border-slate-300"
                      title="Mover para a direita (depois)"
                      data-testid={`hdr-btn-${idx}-down`}
                    >▶</button>
                  </div>
                  <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Botão {idx + 1} · {btn.id}</div>
                </div>
                <label className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={btn.active === true}
                    onChange={(e) => setCfg(setPath(cfg, ["header_actions", "buttons", idx, "active"], e.target.checked))}
                    className="accent-emerald-600"
                    data-testid={`hdr-btn-${idx}-active`}
                  />
                  <span className="font-bold text-slate-700">Marcado como &ldquo;actual&rdquo; (usa cor de destaque)</span>
                </label>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-slate-500">Ícone (nome lucide-react)</Label>
                  <Input value={btn.icon || ""} onChange={(e) => setCfg(setPath(cfg, ["header_actions", "buttons", idx, "icon"], e.target.value))} placeholder="Mail · MessageCircle · Home · Trophy" data-testid={`hdr-btn-${idx}-icon`} />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">URL</Label>
                  <Input value={btn.url || ""} onChange={(e) => setCfg(setPath(cfg, ["header_actions", "buttons", idx, "url"], e.target.value))} placeholder="https://... ou /rota" data-testid={`hdr-btn-${idx}-url`} />
                </div>
              </div>
              <div className="grid sm:grid-cols-4 gap-2">
                <ColorField label="Cor fundo" value={btn.bg || "#0E1740"} onChange={(v) => setCfg(setPath(cfg, ["header_actions", "buttons", idx, "bg"], v))} testid={`hdr-btn-${idx}-bg`} />
                <ColorField label="Cor ícone" value={btn.fg || "#FFFFFF"} onChange={(v) => setCfg(setPath(cfg, ["header_actions", "buttons", idx, "fg"], v))} testid={`hdr-btn-${idx}-fg`} />
                <ColorField label="Fundo (activo)" value={btn.active_bg || "#D6FF00"} onChange={(v) => setCfg(setPath(cfg, ["header_actions", "buttons", idx, "active_bg"], v))} testid={`hdr-btn-${idx}-active-bg`} />
                <ColorField label="Ícone (activo)" value={btn.active_fg || "#0A0E2A"} onChange={(v) => setCfg(setPath(cfg, ["header_actions", "buttons", idx, "active_fg"], v))} testid={`hdr-btn-${idx}-active-fg`} />
              </div>
              <div>
                <Label className="text-[10px] text-slate-500">Etiqueta (aparece por baixo do ícone)</Label>
                <div className="grid sm:grid-cols-3 gap-2">
                  {LANGS.map(({ code, flag }) => (
                    <Input
                      key={code}
                      value={btn[`tooltip_${code}`] || ""}
                      onChange={(e) => setCfg(setPath(cfg, ["header_actions", "buttons", idx, `tooltip_${code}`], e.target.value))}
                      placeholder={`${flag} Ex: EMAIL`}
                      data-testid={`hdr-btn-${idx}-tooltip-${code}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-xs font-bold uppercase text-slate-500 mt-4">Selector de idioma (PT/EN/ES)</div>
        <div className="rounded-lg border border-slate-200 p-3 bg-slate-50/40 grid sm:grid-cols-4 gap-2">
          <ColorField label="Cor de fundo do pill" value={cfg.header_actions?.lang_pill_bg || "#142244"} onChange={(v) => setCfg(setPath(cfg, ["header_actions", "lang_pill_bg"], v))} testid="hdr-lang-pill-bg" />
          <ColorField label="Fundo (idioma activo)" value={cfg.header_actions?.lang_active_bg || "#E5FF00"} onChange={(v) => setCfg(setPath(cfg, ["header_actions", "lang_active_bg"], v))} testid="hdr-lang-active-bg" />
          <ColorField label="Texto (idioma activo)" value={cfg.header_actions?.lang_active_fg || "#0A0E2A"} onChange={(v) => setCfg(setPath(cfg, ["header_actions", "lang_active_fg"], v))} testid="hdr-lang-active-fg" />
          <ColorField label="Texto (inactivo)" value={cfg.header_actions?.lang_inactive_fg || "#FFFFFF"} onChange={(v) => setCfg(setPath(cfg, ["header_actions", "lang_inactive_fg"], v))} testid="hdr-lang-inactive-fg" />
        </div>

        <div className="text-xs font-bold uppercase text-slate-500 mt-4">Botão SAIR</div>
        <div className="rounded-lg border border-slate-200 p-3 bg-slate-50/40 space-y-2">
          <div className="grid sm:grid-cols-3 gap-2">
            {LANGS.map(({ code, flag }) => (
              <div key={code}>
                <Label className="text-[10px] text-slate-500">{flag} Etiqueta</Label>
                <Input
                  value={cfg.header_actions?.[`exit_label_${code}`] || ""}
                  onChange={(e) => setCfg(setPath(cfg, ["header_actions", `exit_label_${code}`], e.target.value))}
                  placeholder={code === "pt" ? "SAIR" : code === "en" ? "EXIT" : "SALIR"}
                  data-testid={`hdr-exit-label-${code}`}
                />
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] text-slate-500">URL de logout</Label>
              <Input value={cfg.header_actions?.exit_url || ""} onChange={(e) => setCfg(setPath(cfg, ["header_actions", "exit_url"], e.target.value))} placeholder="/logout" data-testid="hdr-exit-url" />
            </div>
            <ColorField label="Cor de fundo" value={cfg.header_actions?.exit_bg || "#142244"} onChange={(v) => setCfg(setPath(cfg, ["header_actions", "exit_bg"], v))} testid="hdr-exit-bg" />
            <ColorField label="Cor do ícone" value={cfg.header_actions?.exit_icon_color || "#FF6A00"} onChange={(v) => setCfg(setPath(cfg, ["header_actions", "exit_icon_color"], v))} testid="hdr-exit-icon-color" />
          </div>
        </div>
      </Section>

      {/* NAV MENU */}
      <Section id="nav" icon={Menu} title="Menu de navegação" open={open.nav} onToggle={() => toggleOpen("nav")}>
        <div className="space-y-3">
          {(cfg.nav_items || []).map((it, idx) => (
            <div key={it.id || `nav-${idx}`} className="rounded-lg border border-slate-200 p-3 bg-slate-50/40">
              <div className="grid sm:grid-cols-4 gap-2 mb-2">
                {LANGS.map(({ code, flag }) => (
                  <Input
                    key={code}
                    value={it[`label_${code}`] || ""}
                    onChange={(e) => updateNavItem(idx, `label_${code}`, e.target.value)}
                    placeholder={`${flag} Texto`}
                    data-testid={`nav-${idx}-label-${code}`}
                  />
                ))}
                <Input
                  value={it.url || ""}
                  onChange={(e) => updateNavItem(idx, "url", e.target.value)}
                  placeholder="/url"
                  data-testid={`nav-${idx}-url`}
                />
              </div>
              <button
                onClick={() => removeNavItem(idx)}
                className="text-xs text-rose-600 hover:underline flex items-center gap-1"
                data-testid={`nav-${idx}-remove`}
              ><Trash2 className="w-3.5 h-3.5" /> Remover</button>
            </div>
          ))}
          <Button onClick={addNavItem} variant="outline" className="rounded-full" data-testid="nav-add">
            <Plus className="w-4 h-4 mr-1.5" /> Adicionar item
          </Button>
        </div>

        <hr className="my-3" />
        <div className="text-xs font-bold uppercase text-slate-500 mb-2">Botão pill &quot;{cfg.jogar_online_button?.label_pt || "Jogar Online"}&quot; (canto superior direito)</div>
        <label className="flex items-center gap-2 text-xs cursor-pointer mb-2">
          <input
            type="checkbox"
            checked={cfg.jogar_online_button?.enabled !== false}
            onChange={(e) => setCfg(setPath(cfg, ["jogar_online_button", "enabled"], e.target.checked))}
            className="accent-emerald-600"
            data-testid="jogar-online-enabled"
          />
          <span className="font-bold text-slate-700">Mostrar botão no cabeçalho</span>
        </label>
        <div className="grid sm:grid-cols-4 gap-2">
          {LANGS.map(({ code, flag }) => (
            <Input
              key={code}
              value={cfg.jogar_online_button?.[`label_${code}`] || ""}
              onChange={(e) => setCfg(setPath(cfg, ["jogar_online_button", `label_${code}`], e.target.value))}
              placeholder={`${flag} Texto`}
              data-testid={`jogar-online-label-${code}`}
            />
          ))}
          <Input
            value={cfg.jogar_online_button?.url || ""}
            onChange={(e) => setCfg(setPath(cfg, ["jogar_online_button", "url"], e.target.value))}
            placeholder="/url (deixar vazio → mostra submenu)"
            data-testid="jogar-online-url"
          />
        </div>
        <p className="text-[11px] text-slate-500 mt-1">
          <b>URL principal vazio</b> → o botão abre um submenu com os itens abaixo. Preenchido → vai diretamente para o URL.
        </p>

        {/* SUBMENU — só é usado quando URL principal está vazio */}
        <div className="mt-3 rounded-lg border border-lime-200 bg-lime-50/40 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-700">
              🎮 Submenu (aparece quando o URL principal está vazio)
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const cur = Array.isArray(cfg.jogar_online_button?.submenu) ? cfg.jogar_online_button.submenu : [];
                const next = [...cur, { id: `item_${cur.length + 1}`, label_pt: "", label_en: "", label_es: "", url: "" }];
                setCfg(setPath(cfg, ["jogar_online_button", "submenu"], next));
              }}
              data-testid="jogar-online-submenu-add"
              className="h-7 text-xs"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar item
            </Button>
          </div>
          {(cfg.jogar_online_button?.submenu || []).map((it, idx) => (
            <div key={it.id || idx} className="rounded-md border border-slate-200 bg-white p-2 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                  Item {idx + 1} · {it.id || "(sem id)"}
                </div>
                <button
                  onClick={() => {
                    const cur = [...(cfg.jogar_online_button?.submenu || [])];
                    cur.splice(idx, 1);
                    setCfg(setPath(cfg, ["jogar_online_button", "submenu"], cur));
                  }}
                  className="text-xs text-rose-600 hover:underline flex items-center gap-1"
                  data-testid={`jogar-online-submenu-${idx}-remove`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Remover
                </button>
              </div>
              <div className="grid sm:grid-cols-4 gap-2">
                {LANGS.map(({ code, flag }) => (
                  <Input
                    key={code}
                    value={it[`label_${code}`] || ""}
                    onChange={(e) => setCfg(setPath(cfg, ["jogar_online_button", "submenu", idx, `label_${code}`], e.target.value))}
                    placeholder={`${flag} Ex: VS TEMPO`}
                    data-testid={`jogar-online-submenu-${idx}-label-${code}`}
                  />
                ))}
                <Input
                  value={it.url || ""}
                  onChange={(e) => setCfg(setPath(cfg, ["jogar_online_button", "submenu", idx, "url"], e.target.value))}
                  placeholder="/treinar · /vs-robot · /vs-humano"
                  data-testid={`jogar-online-submenu-${idx}-url`}
                />
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* HERO */}
      <Section id="hero" icon={Sparkles} title="Hero (título + descrição + imagem)" open={open.hero} onToggle={() => toggleOpen("hero")}>
        <MultiLangText
          label="Título (usa Enter para quebrar linhas — ex: APRENDE.\\nJOGA.\\nVENCE!)"
          multiline
          testidPrefix="hero-title"
          value={{ pt: cfg.hero.title_pt, en: cfg.hero.title_en, es: cfg.hero.title_es }}
          onChange={(code, v) => updateHero(code, "title", v)}
        />
        <MultiLangText
          label="Descrição"
          multiline
          testidPrefix="hero-desc"
          value={{ pt: cfg.hero.description_pt, en: cfg.hero.description_en, es: cfg.hero.description_es }}
          onChange={(code, v) => updateHero(code, "description", v)}
        />

        {/* ─────────── Posicionamento vertical (Descrição + CTAs) ─────────── */}
        <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3 space-y-3">
          <div className="text-xs font-bold uppercase text-slate-600">Posicionamento vertical (Descrição + Botões)</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <RangeField
              label="Descrição — deslocamento vertical"
              value={cfg.hero.description_offset_y ?? 0}
              min={-200} max={200} unit="px"
              onChange={(v) => setCfg(setPath(cfg, ["hero", "description_offset_y"], v))}
              testid="hero-description-offset-y"
            />
            <RangeField
              label="Botões CTA — deslocamento vertical"
              value={cfg.hero.ctas_offset_y ?? 0}
              min={-200} max={200} unit="px"
              onChange={(v) => setCfg(setPath(cfg, ["hero", "ctas_offset_y"], v))}
              testid="hero-ctas-offset-y"
            />
          </div>
          <div className="text-[11px] text-slate-500">Valores negativos puxam para cima, positivos empurram para baixo. Não afecta o título nem as imagens.</div>
        </div>

        {/* Hero center mascot + side image — visible no Hero em 3 colunas */}
        <HeroImageUploader
          label="Mascote SUPERTMATIK (coluna central do Hero)"
          field="center_image_url"
          cfg={cfg}
          setCfg={setCfg}
          testid="hero-center-image"
          hint="PNG transparente recomendado · max 8 MB"
        />
        <HeroImageControls
          prefix="center_image"
          cfg={cfg}
          setCfg={setCfg}
          defaults={{ width: 480, glow_color: "#FFE500", glow_intensity: 45 }}
          testid="hero-center"
        />
        <HeroImageUploader
          label="Imagem lateral (coluna direita do Hero — aluna/tablet/etc.)"
          field="side_image_url"
          cfg={cfg}
          setCfg={setCfg}
          testid="hero-side-image"
          hint="PNG transparente recomendado · max 8 MB"
        />
        <HeroImageControls
          prefix="side_image"
          cfg={cfg}
          setCfg={setCfg}
          defaults={{ width: 680, glow_color: "#000000", glow_intensity: 55 }}
          testid="hero-side"
        />

        {/* ──────────────── Vídeo lateral por idioma (substitui a imagem) ──────────────── */}
        <SideVideoPanel cfg={cfg} setCfg={setCfg} />
        {(cfg.hero?.side_video_geometry || "image") === "custom" && (
          <HeroImageControls
            prefix="side_video"
            cfg={cfg}
            setCfg={setCfg}
            defaults={{ width: 680, glow_color: "#3DA5FF", glow_intensity: 40 }}
            testid="hero-side-video"
          />
        )}

        {/* ──────────────── Ecrã da tablet (vídeo demo) ──────────────── */}
        <TabletVideoPanel cfg={cfg} setCfg={setCfg} />

        {/* ──────────────── AI title image generator ──────────────── */}
        <AITitleGenerator cfg={cfg} setCfg={setCfg} />

        {/* ──────────────── Champion Badge (pink starburst) ──────────────── */}
        <ChampionBadgePanel cfg={cfg} setCfg={setCfg} />
      </Section>

      {/* CTAs */}
      <Section id="ctas" icon={Gamepad2} title="Botões CTA (primários + secundários)" open={open.ctas} onToggle={() => toggleOpen("ctas")}>
        {/* ── Posicionamento vertical do bloco de botões CTA ── */}
        <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3 space-y-2">
          <div className="text-xs font-bold uppercase text-slate-600">Posicionamento vertical do bloco</div>
          <RangeField
            label="Deslocamento vertical dos botões CTA"
            value={cfg.hero.ctas_offset_y ?? 0}
            min={-300} max={300} unit="px"
            onChange={(v) => setCfg(setPath(cfg, ["hero", "ctas_offset_y"], v))}
            testid="ctas-section-offset-y"
          />
          <div className="text-[11px] text-slate-500">Valores negativos puxam os botões para cima, positivos empurram para baixo. Espelhado com o slider da secção Hero.</div>
        </div>

        <CtaLayoutPanel cfg={cfg} setCfg={setCfg} />
        <div className="text-xs font-bold uppercase text-slate-500">Botões primários (3 grandes)</div>
        {(cfg.ctas_primary || []).map((c, idx) => (
          <div key={c.id || `primary-${idx}`} className="rounded-lg border border-slate-200 p-3 space-y-2">
            <div className="grid sm:grid-cols-3 gap-2">
              {LANGS.map(({ code, flag }) => (
                <Input
                  key={code}
                  value={c[`label_${code}`] || ""}
                  onChange={(e) => updateCta("ctas_primary", idx, `label_${code}`, e.target.value)}
                  placeholder={`${flag} Texto`}
                  data-testid={`cta-primary-${idx}-label-${code}`}
                />
              ))}
            </div>
            <div className="grid sm:grid-cols-3 gap-2">
              <Input value={c.url || ""} onChange={(e) => updateCta("ctas_primary", idx, "url", e.target.value)} placeholder="/url" data-testid={`cta-primary-${idx}-url`} />
              <Input value={c.color || ""} onChange={(e) => updateCta("ctas_primary", idx, "color", e.target.value)} placeholder="cor (amber/white/indigo)" data-testid={`cta-primary-${idx}-color`} />
              <Input value={c.icon || ""} onChange={(e) => updateCta("ctas_primary", idx, "icon", e.target.value)} placeholder="ícone (School/FileText/Gamepad2)" data-testid={`cta-primary-${idx}-icon`} />
            </div>
            <CtaAdvancedOptions
              cta={c}
              testidPrefix={`cta-primary-${idx}`}
              onChange={(next) => setCfg(setPath(cfg, ["ctas_primary", idx], next))}
            />
          </div>
        ))}

        <div className="text-xs font-bold uppercase text-slate-500 mt-4">Botões secundários (3 pequenos)</div>
        {(cfg.ctas_secondary || []).map((c, idx) => (
          <div key={c.id || `secondary-${idx}`} className="rounded-lg border border-slate-200 p-3 space-y-2">
            <div className="grid sm:grid-cols-3 gap-2">
              {LANGS.map(({ code, flag }) => (
                <Input
                  key={code}
                  value={c[`label_${code}`] || ""}
                  onChange={(e) => updateCta("ctas_secondary", idx, `label_${code}`, e.target.value)}
                  placeholder={`${flag} Texto`}
                  data-testid={`cta-secondary-${idx}-label-${code}`}
                />
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <Input value={c.url || ""} onChange={(e) => updateCta("ctas_secondary", idx, "url", e.target.value)} placeholder="/url" />
              <Input value={c.icon || ""} onChange={(e) => updateCta("ctas_secondary", idx, "icon", e.target.value)} placeholder="ícone" />
            </div>
            <CtaAdvancedOptions
              cta={c}
              testidPrefix={`cta-secondary-${idx}`}
              onChange={(next) => setCfg(setPath(cfg, ["ctas_secondary", idx], next))}
            />
          </div>
        ))}
      </Section>

      {/* PHASES */}
      <Section id="phases" icon={Layers} title="As Fases do Concurso" open={open.phases} onToggle={() => toggleOpen("phases")}>
        <MultiLangText
          label="Título da secção"
          testidPrefix="phases-title"
          value={{ pt: cfg.phases_section.title_pt, en: cfg.phases_section.title_en, es: cfg.phases_section.title_es }}
          onChange={updatePhaseSectionTitle}
        />

        {/* Layout controls — position, dimension, effects of the FASES container */}
        <SectionLayoutControls
          sectionKey="phases_section"
          cfg={cfg}
          setCfg={setCfg}
          showCardFields={false}
          defaults={{ container_width: 0, container_radius: 24, container_bg: "#020B2D", container_glow_intensity: 40 }}
        />

        {/* Global effects for all 3 phase blocks */}
        <div className="rounded-lg border border-purple-200 bg-purple-50/50 p-3 space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-wide text-purple-800">✨ Efeitos globais (FASES)</div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={!!cfg.phases_section?.effect_shimmer} onChange={(e) => setCfg(setPath(cfg, ["phases_section", "effect_shimmer"], e.target.checked))} data-testid="phases-fx-shimmer" />
            <span>Shimmer (varrimento de luz)</span>
          </label>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={!!cfg.phases_section?.effect_pulse_glow} onChange={(e) => setCfg(setPath(cfg, ["phases_section", "effect_pulse_glow"], e.target.checked))} data-testid="phases-fx-pulse" />
            <span>Pulse Glow (respiração do brilho)</span>
          </label>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={!!cfg.phases_section?.effect_bounce_numbers} onChange={(e) => setCfg(setPath(cfg, ["phases_section", "effect_bounce_numbers"], e.target.checked))} data-testid="phases-fx-bounce" />
            <span>Bounce Numbers (1/2/3 saltitam)</span>
          </label>
        </div>


        {(cfg.phases_section?.phases || []).map((p, idx) => (
          <div key={p.id || `phase-${idx}`} className="rounded-lg border border-slate-200 p-3 space-y-2">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-500">
              <span className="bg-sky-100 px-2 py-0.5 rounded">Fase {p.number}</span>
              <span>({p.id})</span>
              <div className="ml-auto flex items-center gap-1">
                <Label className="text-[10px]">N.º:</Label>
                <Input type="number" min={1} max={99} value={p.number || idx + 1} onChange={(e) => updatePhase(idx, "number", Number(e.target.value))} className="w-16 h-7 text-xs" data-testid={`phase-${idx}-number`} />
              </div>
            </div>
            <MultiLangText
              label="Título"
              testidPrefix={`phase-${idx}-title`}
              value={{ pt: p.title_pt, en: p.title_en, es: p.title_es }}
              onChange={(code, v) => updatePhase(idx, `title_${code}`, v)}
            />
            <MultiLangText
              label="Descrição"
              multiline
              testidPrefix={`phase-${idx}-desc`}
              value={{ pt: p.description_pt, en: p.description_en, es: p.description_es }}
              onChange={(code, v) => updatePhase(idx, `description_${code}`, v)}
            />
            <div className="grid sm:grid-cols-2 gap-2">
              <Input value={p.color || ""} onChange={(e) => updatePhase(idx, "color", e.target.value)} placeholder="cor (emerald/orange/sky)" data-testid={`phase-${idx}-color`} />
              <Input value={p.emoji_override || ""} onChange={(e) => updatePhase(idx, "emoji_override", e.target.value)} placeholder="emoji override (🏫 🖥️ 🏆)" data-testid={`phase-${idx}-emoji`} />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Ícone (imagem PNG custom — deixa vazio para usar emoji/default)</Label>
              <div className="flex gap-2 mt-1">
                <Input value={p.icon_url || ""} onChange={(e) => updatePhase(idx, "icon_url", e.target.value)} placeholder="https://… ou /api/static/…" className="flex-1" data-testid={`phase-${idx}-icon-url`} />
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  id={`phase-${idx}-file`}
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 8 * 1024 * 1024) { toast.error("Imagem demasiado grande (máx 8 MB)."); return; }
                    try {
                      const fd = new FormData();
                      fd.append("file", f);
                      const res = await api.post(`/homepage-config/upload-image?kind=phase${idx + 1}_icon`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                      updatePhase(idx, "icon_url", res.data.url);
                      toast.success(`Ícone carregado (${(f.size / 1024).toFixed(0)} KB)`);
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Erro no upload da imagem.");
                    } finally {
                      e.target.value = "";
                    }
                  }}
                />
                <label htmlFor={`phase-${idx}-file`} className="px-2 py-1 rounded-md border border-slate-200 text-xs bg-white hover:bg-slate-50 cursor-pointer inline-flex items-center gap-1">
                  <Upload className="w-3 h-3" /> Carregar
                </label>
                {p.icon_url && (
                  <button type="button" onClick={() => updatePhase(idx, "icon_url", "")} className="px-2 py-1 rounded-md border border-rose-200 text-xs text-rose-600 hover:bg-rose-50" data-testid={`phase-${idx}-icon-clear`}>×</button>
                )}
              </div>
              {p.icon_url && (
                <img src={p.icon_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${p.icon_url}` : p.icon_url} alt="" className="mt-1 h-10 object-contain" />
              )}
            </div>
            <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-end">
              <div>
                <Label className="text-[11px] text-slate-500">Tamanho da imagem desta Fase (px) — 0 = usar padrão global</Label>
                <Input
                  type="number"
                  min={0}
                  max={320}
                  value={p.icon_size ?? 0}
                  onChange={(e) => updatePhase(idx, "icon_size", Number(e.target.value) || 0)}
                  placeholder="0 = auto"
                  data-testid={`phase-${idx}-icon-size`}
                />
              </div>
              {p.icon_size > 0 && (
                <button
                  type="button"
                  onClick={() => updatePhase(idx, "icon_size", 0)}
                  className="px-2 py-1 rounded-md border border-slate-200 text-xs bg-white hover:bg-slate-50"
                  data-testid={`phase-${idx}-icon-size-reset`}
                >
                  Reset
                </button>
              )}
            </div>
            {/* Circle mask presets — apply automatic border-radius + optional
                background + neon ring so the uploaded image looks like the
                Dark/Neon Gaming reference without needing pre-cropped rounded
                PNGs. */}
            <div>
              <Label className="text-[11px] text-slate-500">Máscara / moldura da imagem</Label>
              <select
                value={p.icon_mask || "none"}
                onChange={(e) => updatePhase(idx, "icon_mask", e.target.value)}
                className="w-full h-8 rounded-md border border-slate-200 bg-white text-xs px-2"
                data-testid={`phase-${idx}-icon-mask`}
              >
                <option value="none">Nenhuma (usar imagem tal como é)</option>
                <optgroup label="Básicas">
                  <option value="circle">Círculo simples</option>
                  <option value="rounded">Cantos suavemente arredondados</option>
                </optgroup>
                <optgroup label="Neon (halo brilhante)">
                  <option value="circle_neon">Neon — halo (cor do tema)</option>
                  <option value="circle_lime_electric">Neon — LIMA elétrico ⚡</option>
                  <option value="circle_purple_neon">Neon — ROXO cyberpunk</option>
                  <option value="circle_pink_neon">Neon — ROSA hot pink</option>
                  <option value="circle_cyan_neon">Neon — CIANO ártico</option>
                  <option value="circle_green_neon">Neon — VERDE alien</option>
                </optgroup>
                <optgroup label="Catch-eye (chama a atenção)">
                  <option value="circle_orange">Fundo laranja gaming</option>
                  <option value="circle_navy">Fundo navy neon</option>
                  <option value="circle_rainbow">Aro arco-íris rotativo 🌈</option>
                  <option value="circle_holographic">Holográfico iridescente</option>
                  <option value="circle_double_ring">Duplo anel neon</option>
                  <option value="circle_pulse">Anel neon com pulse animado 💓</option>
                  <option value="circle_dashed">Aro tracejado neon</option>
                </optgroup>
              </select>
              <div className="text-[10px] text-slate-400 mt-1">
                Aplica <code>border-radius</code> + eventual fundo/halo automaticamente na imagem. Combina com fotos quadradas sem precisares de recortar em círculo.
              </div>
            </div>

            {/* ── NOVO: Cores individuais e posição fina do Nº + Título ── */}
            <div className="rounded-md border border-cyan-100 bg-cyan-50/60 p-2 space-y-2">
              <div className="text-[11px] font-bold text-cyan-800 uppercase">Cores individuais desta Fase</div>
              <div className="grid sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px]">Cor do círculo numerado</Label>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="color"
                      value={p.num_color || (["#39E635", "#FFD93D", "#3DA5FF"][idx] || "#3DA5FF")}
                      onChange={(e) => updatePhase(idx, "num_color", e.target.value)}
                      className="w-9 h-8 rounded border"
                      data-testid={`phase-${idx}-num-color`}
                    />
                    <Input
                      value={p.num_color || ""}
                      onChange={(e) => updatePhase(idx, "num_color", e.target.value)}
                      placeholder="deixar vazio = cor padrão"
                      className="font-mono text-[11px] h-8"
                    />
                    {p.num_color && (
                      <button type="button" onClick={() => updatePhase(idx, "num_color", "")} className="text-[10px] text-rose-600 hover:text-rose-700 font-semibold px-1">×</button>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-[11px]">Cor do título</Label>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="color"
                      value={p.title_color || "#FFFFFF"}
                      onChange={(e) => updatePhase(idx, "title_color", e.target.value)}
                      className="w-9 h-8 rounded border"
                      data-testid={`phase-${idx}-title-color`}
                    />
                    <Input
                      value={p.title_color || ""}
                      onChange={(e) => updatePhase(idx, "title_color", e.target.value)}
                      placeholder="deixar vazio = branco"
                      className="font-mono text-[11px] h-8"
                    />
                    {p.title_color && (
                      <button type="button" onClick={() => updatePhase(idx, "title_color", "")} className="text-[10px] text-rose-600 hover:text-rose-700 font-semibold px-1">×</button>
                    )}
                  </div>
                </div>
              </div>

              <div className="text-[11px] font-bold text-cyan-800 uppercase pt-1">Posição fina — Círculo Nº</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px]">Offset X (px)</Label>
                  <Input type="number" min={-200} max={200} value={p.num_offset_x ?? 0} onChange={(e) => updatePhase(idx, "num_offset_x", Number(e.target.value) || 0)} className="h-8 text-xs" data-testid={`phase-${idx}-num-offset-x`} />
                </div>
                <div>
                  <Label className="text-[11px]">Offset Y (px)</Label>
                  <Input type="number" min={-200} max={200} value={p.num_offset_y ?? 0} onChange={(e) => updatePhase(idx, "num_offset_y", Number(e.target.value) || 0)} className="h-8 text-xs" data-testid={`phase-${idx}-num-offset-y`} />
                </div>
              </div>

              <div className="text-[11px] font-bold text-cyan-800 uppercase pt-1">Posição fina — Título</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px]">Offset X (px)</Label>
                  <Input type="number" min={-200} max={200} value={p.title_offset_x ?? 0} onChange={(e) => updatePhase(idx, "title_offset_x", Number(e.target.value) || 0)} className="h-8 text-xs" data-testid={`phase-${idx}-title-offset-x`} />
                </div>
                <div>
                  <Label className="text-[11px]">Offset Y (px)</Label>
                  <Input type="number" min={-200} max={200} value={p.title_offset_y ?? 0} onChange={(e) => updatePhase(idx, "title_offset_y", Number(e.target.value) || 0)} className="h-8 text-xs" data-testid={`phase-${idx}-title-offset-y`} />
                </div>
              </div>

              {(p.num_color || p.title_color || p.num_offset_x || p.num_offset_y || p.title_offset_x || p.title_offset_y) && (
                <button
                  type="button"
                  onClick={() => {
                    updatePhase(idx, "num_color", "");
                    updatePhase(idx, "title_color", "");
                    updatePhase(idx, "num_offset_x", 0);
                    updatePhase(idx, "num_offset_y", 0);
                    updatePhase(idx, "title_offset_x", 0);
                    updatePhase(idx, "title_offset_y", 0);
                  }}
                  className="text-[10px] text-rose-600 hover:text-rose-700 font-semibold underline"
                  data-testid={`phase-${idx}-reset-individual`}
                >
                  Repor tudo desta fase (cores + posições)
                </button>
              )}
            </div>
          </div>
        ))}
      </Section>

      {/* DEADLINE / INSCRIÇÕES BANNER — separate top-level Section */}
      <Section id="deadline" icon={CalendarClock} title="Banner Inscrições até… + Countdown" open={open.deadline} onToggle={() => toggleOpen("deadline")}>
        {/* Editable enrollment deadline banner shown below the 3 phase cards */}
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-3">
          <div className="text-[11px] font-bold uppercase tracking-wide text-amber-800">📅 Banner “Inscrições até…” + Countdown</div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={cfg.phases_section?.deadline_show !== false}
              onChange={(e) => setCfg(setPath(cfg, ["phases_section", "deadline_show"], e.target.checked))}
              data-testid="phases-deadline-show"
            />
            <span className="font-bold">📅 Mostrar badge do prazo de inscrições</span>
          </label>
          <MultiLangText
            label="Texto do banner (deixe vazio para ocultar)"
            testidPrefix="phases-deadline-label"
            placeholder="Ex.: Inscrições até: 01.01.2027"
            value={{
              pt: cfg.phases_section?.deadline_label_pt || "",
              en: cfg.phases_section?.deadline_label_en || "",
              es: cfg.phases_section?.deadline_label_es || "",
            }}
            onChange={(lang, value) => setCfg(setPath(cfg, ["phases_section", `deadline_label_${lang}`], value))}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] font-bold text-slate-700">📆 Data alvo (para countdown, YYYY-MM-DD)</Label>
              <Input
                type="date"
                value={cfg.phases_section?.deadline_target_date || ""}
                onChange={(e) => setCfg(setPath(cfg, ["phases_section", "deadline_target_date"], e.target.value))}
                data-testid="phases-deadline-date"
              />
            </div>
            <div>
              <Label className="text-[11px] font-bold text-slate-700">📐 Alinhamento no ecrã (preset)</Label>
              <select
                value={cfg.phases_section?.deadline_row_align || "center"}
                onChange={(e) => setCfg(setPath(cfg, ["phases_section", "deadline_row_align"], e.target.value))}
                className="w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                data-testid="phases-deadline-align"
              >
                <option value="left">← Esquerda</option>
                <option value="center">↔ Centro</option>
                <option value="right">→ Direita</option>
              </select>
            </div>
          </div>
          <MultiLangText
            label="🔗 URL do banner (link externo — clicável, uma por idioma)"
            testidPrefix="phases-deadline-url"
            placeholder="https://exemplo.pt/inscricao-escola"
            value={{
              pt: cfg.phases_section?.deadline_url_pt || "",
              en: cfg.phases_section?.deadline_url_en || "",
              es: cfg.phases_section?.deadline_url_es || "",
            }}
            onChange={(lang, value) => setCfg(setPath(cfg, ["phases_section", `deadline_url_${lang}`], value))}
          />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label className="text-[11px] font-bold text-slate-700">Fundo (CSS)</Label>
              <Input
                value={cfg.phases_section?.deadline_bg || ""}
                onChange={(e) => setCfg(setPath(cfg, ["phases_section", "deadline_bg"], e.target.value))}
                placeholder="linear-gradient(90deg, #FFD93D 0%, #FFA800 100%)"
                data-testid="phases-deadline-bg"
              />
            </div>
            <div>
              <Label className="text-[11px] font-bold text-slate-700">Cor do texto</Label>
              <Input
                type="color"
                value={cfg.phases_section?.deadline_color || "#0A0E2A"}
                onChange={(e) => setCfg(setPath(cfg, ["phases_section", "deadline_color"], e.target.value))}
                className="h-9 p-1"
                data-testid="phases-deadline-color"
              />
            </div>
            <div>
              <Label className="text-[11px] font-bold text-slate-700">Tamanho (px)</Label>
              <Input
                type="number"
                min={10}
                max={40}
                value={cfg.phases_section?.deadline_size ?? 16}
                onChange={(e) => setCfg(setPath(cfg, ["phases_section", "deadline_size"], Number(e.target.value)))}
                data-testid="phases-deadline-size"
              />
            </div>
            <div>
              <Label className="text-[11px] font-bold text-slate-700">Espaço acima (mt)</Label>
              <Input
                type="number"
                min={0}
                max={120}
                value={cfg.phases_section?.deadline_margin_top ?? 24}
                onChange={(e) => setCfg(setPath(cfg, ["phases_section", "deadline_margin_top"], Number(e.target.value)))}
                data-testid="phases-deadline-mt"
              />
            </div>
          </div>

          {/* Fine positioning offsets X/Y */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] font-bold text-slate-700">↔ Offset X (px)</Label>
              <Input
                type="number"
                min={-400}
                max={400}
                value={cfg.phases_section?.deadline_row_offset_x ?? 0}
                onChange={(e) => setCfg(setPath(cfg, ["phases_section", "deadline_row_offset_x"], Number(e.target.value)))}
                data-testid="phases-deadline-offset-x"
              />
            </div>
            <div>
              <Label className="text-[11px] font-bold text-slate-700">↕ Offset Y (px)</Label>
              <Input
                type="number"
                min={-200}
                max={200}
                value={cfg.phases_section?.deadline_row_offset_y ?? 0}
                onChange={(e) => setCfg(setPath(cfg, ["phases_section", "deadline_row_offset_y"], Number(e.target.value)))}
                data-testid="phases-deadline-offset-y"
              />
            </div>
          </div>

          {/* INSCRIÇÕES CTA button controls (middle pill) */}
          <div className="pt-2 border-t border-amber-300/50 space-y-2">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={cfg.phases_section?.inscricoes_show !== false}
                onChange={(e) => setCfg(setPath(cfg, ["phases_section", "inscricoes_show"], e.target.checked))}
                data-testid="phases-inscricoes-show"
              />
              <span className="font-bold">✏️ Mostrar botão “INSCRIÇÕES” (entre a label e o countdown)</span>
            </label>
            <MultiLangText
              label="Texto do botão (por idioma)"
              testidPrefix="phases-inscricoes-label"
              placeholder="INSCRIÇÕES / REGISTER / INSCRIPCIONES"
              value={{
                pt: cfg.phases_section?.inscricoes_label_pt || "",
                en: cfg.phases_section?.inscricoes_label_en || "",
                es: cfg.phases_section?.inscricoes_label_es || "",
              }}
              onChange={(lang, value) => setCfg(setPath(cfg, ["phases_section", `inscricoes_label_${lang}`], value))}
            />
            <MultiLangText
              label="🔗 URL do botão (por idioma — abre em nova aba)"
              testidPrefix="phases-inscricoes-url"
              placeholder="https://forms.google.com/…"
              value={{
                pt: cfg.phases_section?.inscricoes_url_pt || "",
                en: cfg.phases_section?.inscricoes_url_en || "",
                es: cfg.phases_section?.inscricoes_url_es || "",
              }}
              onChange={(lang, value) => setCfg(setPath(cfg, ["phases_section", `inscricoes_url_${lang}`], value))}
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-[11px] font-bold text-slate-700">Fundo (CSS)</Label>
                <Input
                  value={cfg.phases_section?.inscricoes_bg || ""}
                  onChange={(e) => setCfg(setPath(cfg, ["phases_section", "inscricoes_bg"], e.target.value))}
                  placeholder="linear-gradient(90deg, #22C55E 0%, #15803D 100%)"
                  data-testid="phases-inscricoes-bg"
                />
              </div>
              <div>
                <Label className="text-[11px] font-bold text-slate-700">Cor do texto</Label>
                <Input
                  type="color"
                  value={cfg.phases_section?.inscricoes_color || "#FFFFFF"}
                  onChange={(e) => setCfg(setPath(cfg, ["phases_section", "inscricoes_color"], e.target.value))}
                  className="h-9 p-1"
                  data-testid="phases-inscricoes-color"
                />
              </div>
              <div>
                <Label className="text-[11px] font-bold text-slate-700">Tamanho (px)</Label>
                <Input
                  type="number"
                  min={10}
                  max={40}
                  value={cfg.phases_section?.inscricoes_size ?? 16}
                  onChange={(e) => setCfg(setPath(cfg, ["phases_section", "inscricoes_size"], Number(e.target.value)))}
                  data-testid="phases-inscricoes-size"
                />
              </div>
            </div>
          </div>

          {/* Countdown pill controls */}
          <div className="pt-2 border-t border-amber-300/50 space-y-2">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={cfg.phases_section?.countdown_show !== false}
                onChange={(e) => setCfg(setPath(cfg, ["phases_section", "countdown_show"], e.target.checked))}
                data-testid="phases-countdown-show"
              />
              <span className="font-bold">⏳ Mostrar pill de countdown ao lado (requer data alvo definida)</span>
            </label>
            <MultiLangText
              label="Rótulo antes do número (ex.: FALTAM / ONLY / FALTAN)"
              testidPrefix="phases-countdown-label"
              value={{
                pt: cfg.phases_section?.countdown_label_pt || "",
                en: cfg.phases_section?.countdown_label_en || "",
                es: cfg.phases_section?.countdown_label_es || "",
              }}
              onChange={(lang, value) => setCfg(setPath(cfg, ["phases_section", `countdown_label_${lang}`], value))}
            />
            <MultiLangText
              label="Sufixo depois do número (ex.: DIAS / DAYS LEFT / DÍAS)"
              testidPrefix="phases-countdown-suffix"
              value={{
                pt: cfg.phases_section?.countdown_suffix_pt || "",
                en: cfg.phases_section?.countdown_suffix_en || "",
                es: cfg.phases_section?.countdown_suffix_es || "",
              }}
              onChange={(lang, value) => setCfg(setPath(cfg, ["phases_section", `countdown_suffix_${lang}`], value))}
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-[11px] font-bold text-slate-700">Fundo (CSS)</Label>
                <Input
                  value={cfg.phases_section?.countdown_bg || ""}
                  onChange={(e) => setCfg(setPath(cfg, ["phases_section", "countdown_bg"], e.target.value))}
                  placeholder="linear-gradient(90deg, #3DA5FF 0%, #6E5BFF 100%)"
                  data-testid="phases-countdown-bg"
                />
              </div>
              <div>
                <Label className="text-[11px] font-bold text-slate-700">Cor do texto</Label>
                <Input
                  type="color"
                  value={cfg.phases_section?.countdown_color || "#FFFFFF"}
                  onChange={(e) => setCfg(setPath(cfg, ["phases_section", "countdown_color"], e.target.value))}
                  className="h-9 p-1"
                  data-testid="phases-countdown-color"
                />
              </div>
              <div>
                <Label className="text-[11px] font-bold text-slate-700">Tamanho (px)</Label>
                <Input
                  type="number"
                  min={10}
                  max={40}
                  value={cfg.phases_section?.countdown_size ?? 16}
                  onChange={(e) => setCfg(setPath(cfg, ["phases_section", "countdown_size"], Number(e.target.value)))}
                  data-testid="phases-countdown-size"
                />
              </div>
            </div>
          </div>

          {/* Store button (external online shop) — sits to the right of the
              countdown when active. Same layout primitive as the others so
              the row stays auto-centered even when some pills are hidden. */}
          <div className="pt-2 border-t border-amber-300/50 space-y-2">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={cfg.phases_section?.store_show === true}
                onChange={(e) => setCfg(setPath(cfg, ["phases_section", "store_show"], e.target.checked))}
                data-testid="phases-store-show"
              />
              <span className="font-bold">🛒 Mostrar botão STORE (loja online externa)</span>
            </label>
            <MultiLangText
              label="Texto do botão (ex.: SUPERTMATIK STORE)"
              testidPrefix="phases-store-label"
              value={{
                pt: cfg.phases_section?.store_label_pt || "",
                en: cfg.phases_section?.store_label_en || "",
                es: cfg.phases_section?.store_label_es || "",
              }}
              onChange={(lang, value) => setCfg(setPath(cfg, ["phases_section", `store_label_${lang}`], value))}
            />
            <MultiLangText
              label="URL da loja por idioma (ex.: https://store.supertmatik.com)"
              testidPrefix="phases-store-url"
              placeholder="https://…"
              value={{
                pt: cfg.phases_section?.store_url_pt || "",
                en: cfg.phases_section?.store_url_en || "",
                es: cfg.phases_section?.store_url_es || "",
              }}
              onChange={(lang, value) => setCfg(setPath(cfg, ["phases_section", `store_url_${lang}`], value))}
            />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <Label className="text-[11px] font-bold text-slate-700">Ícone (emoji)</Label>
                <Input
                  value={cfg.phases_section?.store_icon ?? "🛒"}
                  onChange={(e) => setCfg(setPath(cfg, ["phases_section", "store_icon"], e.target.value))}
                  placeholder="🛒"
                  data-testid="phases-store-icon"
                />
              </div>
              <div>
                <Label className="text-[11px] font-bold text-slate-700">Fundo (CSS)</Label>
                <Input
                  value={cfg.phases_section?.store_bg || ""}
                  onChange={(e) => setCfg(setPath(cfg, ["phases_section", "store_bg"], e.target.value))}
                  placeholder="linear-gradient(90deg, #FF6A00 0%, #EE0979 100%)"
                  data-testid="phases-store-bg"
                />
              </div>
              <div>
                <Label className="text-[11px] font-bold text-slate-700">Cor do texto</Label>
                <Input
                  type="color"
                  value={cfg.phases_section?.store_color || "#FFFFFF"}
                  onChange={(e) => setCfg(setPath(cfg, ["phases_section", "store_color"], e.target.value))}
                  className="h-9 p-1"
                  data-testid="phases-store-color"
                />
              </div>
              <div>
                <Label className="text-[11px] font-bold text-slate-700">Tamanho (px)</Label>
                <Input
                  type="number"
                  min={10}
                  max={40}
                  value={cfg.phases_section?.store_size ?? 16}
                  onChange={(e) => setCfg(setPath(cfg, ["phases_section", "store_size"], Number(e.target.value)))}
                  data-testid="phases-store-size"
                />
              </div>
            </div>
          </div>
        </div>
      </Section>


      {/* ONLINE CARDS */}
      <Section id="online" icon={Gamepad2} title="Joga Online (3 cards de modo)" open={open.online} onToggle={() => toggleOpen("online")}>
        <MultiLangText
          label="Título da secção"
          testidPrefix="online-title"
          value={{ pt: cfg.online_section.title_pt, en: cfg.online_section.title_en, es: cfg.online_section.title_es }}
          onChange={updateOnlineSectionTitle}
        />

        {/* Layout controls — position, dimension, effects of the container + inner cards */}
        <SectionLayoutControls
          sectionKey="online_section"
          cfg={cfg}
          setCfg={setCfg}
          showCardFields={true}
          defaults={{ container_width: 580, container_radius: 28, container_bg: "#050D2E", container_glow_intensity: 40 }}
        />

        {/* Global effects for all 3 online cards */}
        <div className="rounded-lg border border-purple-200 bg-purple-50/50 p-3 space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-wide text-purple-800">✨ Efeitos globais (JOGA ONLINE)</div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={!!cfg.online_section?.effect_shimmer} onChange={(e) => setCfg(setPath(cfg, ["online_section", "effect_shimmer"], e.target.checked))} data-testid="online-fx-shimmer" />
            <span>Shimmer (varrimento de luz nos cards)</span>
          </label>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={!!cfg.online_section?.effect_pulse_glow} onChange={(e) => setCfg(setPath(cfg, ["online_section", "effect_pulse_glow"], e.target.checked))} data-testid="online-fx-pulse" />
            <span>Pulse Glow (respiração do brilho)</span>
          </label>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={!!cfg.online_section?.effect_neon_flicker} onChange={(e) => setCfg(setPath(cfg, ["online_section", "effect_neon_flicker"], e.target.checked))} data-testid="online-fx-flicker" />
            <span>Neon Flicker (ligeiro tremor no néon)</span>
          </label>
        </div>

        {/* Button size — presets + manual width/height sliders */}
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-wide text-emerald-800">Botão JOGAR — tamanho</div>
          <div className="flex flex-wrap gap-2">
            {[
              { label: "S · Pequeno", w: 100, h: 34, size: 13 },
              { label: "M · Médio",   w: 140, h: 42, size: 16 },
              { label: "L · Grande",  w: 180, h: 52, size: 18 },
              { label: "XL · Extra",  w: 220, h: 60, size: 22 },
              { label: "Full · Largura total", w: 260, h: 48, size: 18 },
            ].map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => setCfg(setPath(setPath(setPath(cfg, ["online_section", "card_button_w"], p.w), ["online_section", "card_button_h"], p.h), ["online_section", "card_button_size"], p.size))}
                className="px-3 py-1.5 rounded-md text-[11px] font-bold bg-emerald-500 hover:bg-emerald-600 text-white transition"
                data-testid={`online-btn-preset-${p.label.split(" ")[0].toLowerCase()}`}
              >{p.label}</button>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px]">Largura ({cfg.online_section?.card_button_w ?? 140}px)</Label>
              <Input type="range" min={80} max={340} value={cfg.online_section?.card_button_w ?? 140} onChange={(e) => setCfg(setPath(cfg, ["online_section", "card_button_w"], Number(e.target.value)))} data-testid="online-btn-width" />
            </div>
            <div>
              <Label className="text-[10px]">Altura ({cfg.online_section?.card_button_h ?? 42}px)</Label>
              <Input type="range" min={28} max={80} value={cfg.online_section?.card_button_h ?? 42} onChange={(e) => setCfg(setPath(cfg, ["online_section", "card_button_h"], Number(e.target.value)))} data-testid="online-btn-height" />
            </div>
            <div>
              <Label className="text-[10px]">Tamanho texto ({cfg.online_section?.card_button_size ?? 16}px)</Label>
              <Input type="range" min={10} max={32} value={cfg.online_section?.card_button_size ?? 16} onChange={(e) => setCfg(setPath(cfg, ["online_section", "card_button_size"], Number(e.target.value)))} data-testid="online-btn-fontsize" />
            </div>
          </div>
        </div>

        {(cfg.online_section?.cards || []).map((c, idx) => (
          <div key={c.id || `online-${idx}`} className="rounded-lg border border-slate-200 p-3 space-y-2">
            <div className="text-xs font-bold text-slate-500 uppercase">Card {idx + 1} ({c.id})</div>
            <MultiLangText
              label="Título"
              testidPrefix={`online-${idx}-title`}
              value={{ pt: c.title_pt, en: c.title_en, es: c.title_es }}
              onChange={(code, v) => updateOnlineCard(idx, `title_${code}`, v)}
            />
            <MultiLangText
              label="Descrição"
              multiline
              testidPrefix={`online-${idx}-desc`}
              value={{ pt: c.description_pt, en: c.description_en, es: c.description_es }}
              onChange={(code, v) => updateOnlineCard(idx, `description_${code}`, v)}
            />
            <MultiLangText
              label="Texto do botão"
              testidPrefix={`online-${idx}-btn`}
              value={{ pt: c.button_pt, en: c.button_en, es: c.button_es }}
              onChange={(code, v) => updateOnlineCard(idx, `button_${code}`, v)}
            />
            <div className="grid sm:grid-cols-3 gap-2">
              <Input value={c.url || ""} onChange={(e) => updateOnlineCard(idx, "url", e.target.value)} placeholder="/url" data-testid={`online-${idx}-url`} />
              <Input value={c.color || ""} onChange={(e) => updateOnlineCard(idx, "color", e.target.value)} placeholder="cor (blue/green/violet)" data-testid={`online-${idx}-color`} />
              <Input value={c.icon || ""} onChange={(e) => updateOnlineCard(idx, "icon", e.target.value)} placeholder="ícone Lucide (Clock/Bot/Users)" data-testid={`online-${idx}-icon`} />
            </div>
            {idx === 0 && (
              <div className="rounded-md bg-violet-50 border border-violet-200 p-2">
                <Label className="text-[11px] font-bold text-violet-700">Link do badge DEMO (opcional)</Label>
                <Input
                  value={c.demo_badge_url || ""}
                  onChange={(e) => updateOnlineCard(idx, "demo_badge_url", e.target.value)}
                  placeholder="/jogar/... ou https://..."
                  data-testid={`online-${idx}-demo-url`}
                />
                <div className="text-[10px] text-slate-500 mt-1">O badge "DEMO" é sempre clicável. Se vazio, abre a demonstração VS TEMPO (/demo) por omissão. Se preenchido, usa este link — interno (começa por /) abre na app; externo (http...) abre em nova aba.</div>
              </div>
            )}
            {/* Custom icon (image or emoji override) */}
            <div className="grid sm:grid-cols-2 gap-2">
              <Input value={c.emoji_override || ""} onChange={(e) => updateOnlineCard(idx, "emoji_override", e.target.value)} placeholder="emoji override (opcional: ⏱️ 🤖 👥)" data-testid={`online-${idx}-emoji`} />
              <div className="flex gap-1">
                <Input value={c.icon_url || ""} onChange={(e) => updateOnlineCard(idx, "icon_url", e.target.value)} placeholder="URL imagem custom (opcional)" className="flex-1" data-testid={`online-${idx}-iconurl`} />
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  id={`online-${idx}-file`}
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 8 * 1024 * 1024) { toast.error("Imagem demasiado grande (máx 8 MB)."); return; }
                    try {
                      const fd = new FormData();
                      fd.append("file", f);
                      const res = await api.post(`/homepage-config/upload-image?kind=online${idx + 1}_icon`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                      updateOnlineCard(idx, "icon_url", res.data.url);
                      toast.success(`Ícone carregado (${(f.size / 1024).toFixed(0)} KB)`);
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Erro no upload da imagem.");
                    } finally {
                      e.target.value = "";
                    }
                  }}
                />
                <label htmlFor={`online-${idx}-file`} className="px-2 py-1 rounded-md border border-slate-200 text-xs bg-white hover:bg-slate-50 cursor-pointer inline-flex items-center gap-1">
                  <Upload className="w-3 h-3" /> Ficheiro
                </label>
                {c.icon_url && (
                  <button type="button" onClick={() => updateOnlineCard(idx, "icon_url", "")} className="px-2 py-1 rounded-md border border-rose-200 text-xs text-rose-600" data-testid={`online-${idx}-icon-clear`}>×</button>
                )}
              </div>
            </div>
            {c.icon_url && (
              <img src={c.icon_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${c.icon_url}` : c.icon_url} alt="" className="h-10 object-contain" />
            )}
          </div>
        ))}
      </Section>

      {/* ONLINE SECUNDÁRIO — bloco autónomo */}
      <Section id="online_secondary" icon={Gamepad2} title="Bloco Vídeo" open={open.online_secondary} onToggle={() => toggleOpen("online_secondary")}>
        <div className="rounded-lg border-2 border-lime-300 bg-lime-50/50 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-bold uppercase tracking-wide text-lime-800">🎬 Bloco Vídeo</div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={!!cfg.online_secondary_section?.enabled}
                onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "enabled"], e.target.checked))}
                data-testid="online-sec-enabled"
              />
              <span className="font-bold">Mostrar</span>
            </label>
          </div>
          <p className="text-[10px] text-lime-800/80">Versão compacta do &quot;JOGA ONLINE&quot; — aparece imediatamente abaixo do bloco principal. Herda os cards por defeito; podes desativar a herança e configurar cards independentes.</p>

          {/* ── Título "APRENDER A JOGAR" + Mancha pintada (banner) ───────── */}
          <div className="rounded border-2 border-blue-300 bg-blue-50/60 p-2 space-y-2">
            <div className="text-[10px] font-bold uppercase tracking-wide text-blue-800">🖌️ Título &quot;APRENDER A JOGAR&quot; · Mancha pintada · Tamanho · Forma · Posição</div>
            <MultiLangText
              label="Título do banner (deixa vazio para esconder)"
              testidPrefix="online-sec-title"
              value={{
                pt: cfg.online_secondary_section?.title_pt || "",
                en: cfg.online_secondary_section?.title_en || "",
                es: cfg.online_secondary_section?.title_es || "",
              }}
              onChange={(code, v) => setCfg(setPath(cfg, ["online_secondary_section", `title_${code}`], v))}
            />
            <div className="grid grid-cols-3 gap-2">
              <ColorField label="Cor da mancha (azul)" value={cfg.online_secondary_section?.banner_paint_color || "#1B6FD1"} onChange={(v) => setCfg(setPath(cfg, ["online_secondary_section", "banner_paint_color"], v))} testid="online-sec-banner-paint" />
              <ColorField label="Cor dos salpicos (amarelo)" value={cfg.online_secondary_section?.banner_splatter_color || "#FFD93D"} onChange={(v) => setCfg(setPath(cfg, ["online_secondary_section", "banner_splatter_color"], v))} testid="online-sec-banner-splatter" />
              <label className="flex items-end gap-1.5 text-xs cursor-pointer pb-1">
                <input
                  type="checkbox"
                  checked={cfg.online_secondary_section?.banner_show_splatter !== false}
                  onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "banner_show_splatter"], e.target.checked))}
                  data-testid="online-sec-banner-show-splatter"
                />
                Mostrar salpicos
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px]">Largura (px · 0 = auto)</Label>
                <Input type="number" min={0} max={1000} value={cfg.online_secondary_section?.banner_width ?? 0} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "banner_width"], Number(e.target.value)))} className="h-8 text-xs" data-testid="online-sec-banner-w" />
              </div>
              <div>
                <Label className="text-[10px]">Altura (px · 0 = auto)</Label>
                <Input type="number" min={0} max={400} value={cfg.online_secondary_section?.banner_height ?? 0} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "banner_height"], Number(e.target.value)))} className="h-8 text-xs" data-testid="online-sec-banner-h" />
              </div>
              <div>
                <Label className="text-[10px]">Padding X ({cfg.online_secondary_section?.banner_padding_x ?? 40}px)</Label>
                <Input type="range" min={0} max={200} value={cfg.online_secondary_section?.banner_padding_x ?? 40} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "banner_padding_x"], Number(e.target.value)))} data-testid="online-sec-banner-px" />
              </div>
              <div>
                <Label className="text-[10px]">Padding Y ({cfg.online_secondary_section?.banner_padding_y ?? 10}px)</Label>
                <Input type="range" min={0} max={80} value={cfg.online_secondary_section?.banner_padding_y ?? 10} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "banner_padding_y"], Number(e.target.value)))} data-testid="online-sec-banner-py" />
              </div>
              <div>
                <Label className="text-[10px]">Offset X ({cfg.online_secondary_section?.banner_offset_x ?? 0}px)</Label>
                <Input type="range" min={-300} max={300} value={cfg.online_secondary_section?.banner_offset_x ?? 0} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "banner_offset_x"], Number(e.target.value)))} data-testid="online-sec-banner-ox" />
              </div>
              <div>
                <Label className="text-[10px]">Offset Y ({cfg.online_secondary_section?.banner_offset_y ?? 0}px)</Label>
                <Input type="range" min={-200} max={200} value={cfg.online_secondary_section?.banner_offset_y ?? 0} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "banner_offset_y"], Number(e.target.value)))} data-testid="online-sec-banner-oy" />
              </div>
              <div>
                <Label className="text-[10px]">Escala ({(cfg.online_secondary_section?.banner_scale ?? 1).toFixed(2)}×)</Label>
                <Input type="range" min={0.4} max={2.5} step={0.05} value={cfg.online_secondary_section?.banner_scale ?? 1} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "banner_scale"], Number(e.target.value)))} data-testid="online-sec-banner-scale" />
              </div>
              <div>
                <Label className="text-[10px]">Rugosidade ({cfg.online_secondary_section?.banner_roughness ?? 10})</Label>
                <Input type="range" min={0} max={30} step={1} value={cfg.online_secondary_section?.banner_roughness ?? 10} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "banner_roughness"], Number(e.target.value)))} data-testid="online-sec-banner-rough" />
              </div>
            </div>
          </div>

          {/* ── Agendador: só mostrar entre datas X e Y ─────────────── */}
          <div className="rounded border-2 border-amber-300 bg-amber-50/50 p-2 space-y-1.5">
            <div className="text-[10px] font-bold uppercase tracking-wide text-amber-800">⏰ Agendador (opcional)</div>
            <p className="text-[10px] text-amber-800/80">Deixa vazio para mostrar sempre. Preenche para o bloco só aparecer entre estas datas (ideal para promoções sazonais).</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px]">Aparecer a partir de</Label>
                <Input
                  type="datetime-local"
                  value={cfg.online_secondary_section?.visible_from || ""}
                  onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "visible_from"], e.target.value))}
                  data-testid="online-sec-from"
                  className="h-8 text-xs"
                />
              </div>
              <div>
                <Label className="text-[10px]">Desaparecer em</Label>
                <Input
                  type="datetime-local"
                  value={cfg.online_secondary_section?.visible_until || ""}
                  onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "visible_until"], e.target.value))}
                  data-testid="online-sec-until"
                  className="h-8 text-xs"
                />
              </div>
            </div>
            {(cfg.online_secondary_section?.visible_from || cfg.online_secondary_section?.visible_until) && (
              <button
                type="button"
                onClick={() => setCfg(setPath(setPath(cfg, ["online_secondary_section", "visible_from"], ""), ["online_secondary_section", "visible_until"], ""))}
                className="text-[10px] text-amber-700 hover:text-amber-900 underline"
                data-testid="online-sec-clear-schedule"
              >Limpar agendamento</button>
            )}
            {/* Status hint */}
            {(() => {
              const from = cfg.online_secondary_section?.visible_from;
              const until = cfg.online_secondary_section?.visible_until;
              if (!from && !until) return null;
              const now = Date.now();
              const fromMs = from ? Date.parse(from) : null;
              const untilMs = until ? Date.parse(until) : null;
              let status = "✅ Visível agora";
              if (fromMs && now < fromMs) status = `⏳ Aparece em ${new Date(fromMs).toLocaleString("pt-PT")}`;
              else if (untilMs && now > untilMs) status = `❌ Já expirou (${new Date(untilMs).toLocaleString("pt-PT")})`;
              return <div className="text-[10px] font-bold text-amber-900">{status}</div>;
            })()}

            {/* ── Seasonal effects — decorative particles inside the block ── */}
            <div className="border-t border-amber-200 pt-2 mt-1 space-y-1.5">
              <div className="text-[10px] font-bold uppercase tracking-wide text-amber-800">🎨 Efeito sazonal (opcional)</div>
              <div className="grid grid-cols-4 gap-1">
                {[
                  { v: "none",       label: "Nenhum",     icon: "✖" },
                  { v: "snow",       label: "Neve",       icon: "❄️" },
                  { v: "halloween",  label: "Halloween",  icon: "🎃" },
                  { v: "valentines", label: "Namorados",  icon: "💖" },
                  { v: "party",      label: "Festa",      icon: "🎉" },
                  { v: "fireworks",  label: "Fogo-artif", icon: "✨" },
                  { v: "autumn",     label: "Outono",     icon: "🍂" },
                  { v: "spring",     label: "Primavera",  icon: "🌸" },
                  { v: "summer",     label: "Verão",      icon: "☀️" },
                ].map((opt) => {
                  const active = (cfg.online_secondary_section?.seasonal_effect || "none") === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setCfg(setPath(cfg, ["online_secondary_section", "seasonal_effect"], opt.v))}
                      className={`rounded border px-1 py-1.5 text-[10px] font-bold text-center transition ${active ? "bg-amber-500 text-white border-amber-600 shadow-sm" : "bg-white border-amber-200 text-amber-900 hover:bg-amber-100"}`}
                      data-testid={`online-sec-fx-${opt.v}`}
                    >
                      <div className="text-base leading-none">{opt.icon}</div>
                      <div>{opt.label}</div>
                    </button>
                  );
                })}
              </div>
              {(cfg.online_secondary_section?.seasonal_effect || "none") !== "none" && (
                <div>
                  <Label className="text-[10px]">Intensidade / partículas ({cfg.online_secondary_section?.seasonal_intensity ?? 60}%)</Label>
                  <Input
                    type="range" min={10} max={100} step={5}
                    value={cfg.online_secondary_section?.seasonal_intensity ?? 60}
                    onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "seasonal_intensity"], Number(e.target.value)))}
                    data-testid="online-sec-fx-intensity"
                  />
                </div>
              )}
            </div>

            {/* ── Video mode — overrides all cards with a promo video ── */}
            <div className="border-t border-amber-200 pt-2 mt-1 space-y-1.5">
              <label className="flex items-center gap-2 text-xs font-bold cursor-pointer text-amber-900">
                <input
                  type="checkbox"
                  checked={!!cfg.online_secondary_section?.video_mode}
                  onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "video_mode"], e.target.checked))}
                  data-testid="online-sec-video-mode"
                />
                <span>🎬 Modo VÍDEO (substitui cards por um vídeo)</span>
              </label>
              {cfg.online_secondary_section?.video_mode && (
                <div className="space-y-2 rounded border border-amber-300 bg-white p-2">
                  <div className="text-[10px] font-black uppercase tracking-wide text-amber-800">🌐 Vídeo por idioma — cada língua tem o seu próprio vídeo & poster</div>
                  <p className="text-[10px] text-amber-800/70 italic">O sistema mostra o vídeo do idioma do visitante. Se um idioma ficar vazio, cai automaticamente no PT (padrão).</p>
                  {LANGS.map(({ code, flag, label }) => (
                    <div key={code} className="rounded border border-amber-200 bg-amber-50/40 p-2 space-y-1.5" data-testid={`online-sec-video-lang-${code}`}>
                      <div className="text-[11px] font-bold text-amber-900 flex items-center gap-1"><span>{flag}</span> {label}</div>
                      {/* URL do vídeo */}
                      <div>
                        <Label className="text-[10px]">URL do vídeo (YouTube, .mp4, .webm)</Label>
                        <div className="flex gap-1">
                          <Input
                            value={cfg.online_secondary_section?.[`video_url_${code}`] || ""}
                            onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", `video_url_${code}`], e.target.value))}
                            placeholder={`https://youtube.com/watch?v=… ou https://…mp4 (${label})`}
                            className="h-8 text-xs flex-1"
                            data-testid={`online-sec-video-url-${code}`}
                          />
                          <input
                            type="file"
                            accept="video/*"
                            id={`online-sec-video-file-${code}`}
                            className="hidden"
                            onChange={async (e) => {
                              const f = e.target.files?.[0];
                              if (!f) return;
                              if (f.size > 50 * 1024 * 1024) { toast.error("Vídeo demasiado grande (máx 50 MB). Usa antes um link YouTube."); return; }
                              try {
                                const fd = new FormData();
                                fd.append("file", f);
                                const res = await api.post("/homepage-config/upload-video", fd, {
                                  headers: { "Content-Type": "multipart/form-data" },
                                });
                                const url = res?.data?.url;
                                if (!url) throw new Error("Sem URL");
                                setCfg(setPath(cfg, ["online_secondary_section", `video_url_${code}`], url));
                                toast.success(`Vídeo ${label} carregado.`);
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "Falha ao carregar vídeo");
                              } finally {
                                e.target.value = "";
                              }
                            }}
                          />
                          <label htmlFor={`online-sec-video-file-${code}`} className="px-2 py-1 rounded border border-amber-300 bg-white text-xs cursor-pointer hover:bg-amber-50 inline-flex items-center gap-1">
                            <Upload className="w-3 h-3" /> Ficheiro
                          </label>
                        </div>
                      </div>
                      {/* Poster do vídeo */}
                      <div>
                        <Label className="text-[10px]">Imagem de capa (poster)</Label>
                        <div className="flex gap-1">
                          <Input
                            value={cfg.online_secondary_section?.[`video_poster_${code}`] || ""}
                            onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", `video_poster_${code}`], e.target.value))}
                            placeholder="/api/static/... ou URL"
                            className="h-8 text-xs flex-1"
                            data-testid={`online-sec-video-poster-${code}`}
                          />
                          <input
                            type="file"
                            accept="image/*"
                            id={`online-sec-poster-file-${code}`}
                            className="hidden"
                            onChange={async (e) => {
                              const f = e.target.files?.[0];
                              if (!f) return;
                              if (f.size > 8 * 1024 * 1024) { toast.error("Imagem demasiado grande (máx 8 MB)."); return; }
                              try {
                                const fd = new FormData();
                                fd.append("file", f);
                                const res = await api.post("/homepage-config/upload-image?kind=poster", fd, {
                                  headers: { "Content-Type": "multipart/form-data" },
                                });
                                const url = res?.data?.url;
                                if (!url) throw new Error("Sem URL");
                                setCfg(setPath(cfg, ["online_secondary_section", `video_poster_${code}`], url));
                                toast.success(`Poster ${label} carregado.`);
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "Falha ao carregar imagem");
                              } finally {
                                e.target.value = "";
                              }
                            }}
                          />
                          <label htmlFor={`online-sec-poster-file-${code}`} className="px-2 py-1 rounded border border-amber-300 bg-white text-xs cursor-pointer hover:bg-amber-50 inline-flex items-center gap-1">
                            <Upload className="w-3 h-3" /> Ficheiro
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div>
                    <Label className="text-[10px]">Formato (comum a todas as línguas)</Label>
                    <select
                      value={cfg.online_secondary_section?.video_aspect || "16/9"}
                      onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "video_aspect"], e.target.value))}
                      className="w-full h-8 rounded border border-slate-200 bg-white px-1 text-xs"
                      data-testid="online-sec-video-aspect"
                    >
                      <option value="16/9">16:9 (widescreen)</option>
                      <option value="4/3">4:3</option>
                      <option value="21/9">21:9 (cinema)</option>
                      <option value="1/1">1:1 (quadrado)</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-1 text-[10px]">
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={cfg.online_secondary_section?.video_autoplay !== false} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "video_autoplay"], e.target.checked))} data-testid="online-sec-video-autoplay" /> Autoplay
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={cfg.online_secondary_section?.video_muted !== false} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "video_muted"], e.target.checked))} data-testid="online-sec-video-muted" /> Silenciado (necessário para autoplay)
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={cfg.online_secondary_section?.video_loop !== false} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "video_loop"], e.target.checked))} data-testid="online-sec-video-loop" /> Loop
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={cfg.online_secondary_section?.video_controls !== false} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "video_controls"], e.target.checked))} data-testid="online-sec-video-controls" /> Mostrar controlos
                    </label>
                  </div>
                  <p className="text-[10px] text-amber-800/80 italic">Nota: com Autoplay activo, o vídeo tem de estar Silenciado (regra dos browsers).</p>

                  {/* Posicionamento vertical do vídeo dentro da caixa */}
                  <div className="mt-2 rounded-md border border-blue-200 bg-blue-50/40 p-2 space-y-2">
                    <div className="text-[10px] font-bold uppercase tracking-wide text-blue-800">📐 Posição vertical do vídeo</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px]">
                          Distância ao título ({cfg.online_secondary_section?.video_offset_y ?? 0}px)
                        </Label>
                        <Input
                          type="range"
                          min={-80}
                          max={200}
                          step={1}
                          value={cfg.online_secondary_section?.video_offset_y ?? 0}
                          onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "video_offset_y"], Number(e.target.value)))}
                          data-testid="online-sec-video-offset-y"
                        />
                      </div>
                      <div>
                        <Label className="text-[10px]">
                          Espaço abaixo do vídeo ({cfg.online_secondary_section?.video_bottom_gap ?? 0}px)
                        </Label>
                        <Input
                          type="range"
                          min={0}
                          max={200}
                          step={1}
                          value={cfg.online_secondary_section?.video_bottom_gap ?? 0}
                          onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "video_bottom_gap"], Number(e.target.value)))}
                          data-testid="online-sec-video-bottom-gap"
                        />
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-500">
                      <b>Distância ao título</b> empurra o vídeo para baixo (aumentar valor) ou aproxima do título (valor negativo).
                      <b> Espaço abaixo</b> aumenta a folga entre o limite inferior do vídeo e o rebordo inferior da caixa.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px]">Escala ({cfg.online_secondary_section?.scale ?? 65}%)</Label>
              <Input type="range" min={50} max={100} value={cfg.online_secondary_section?.scale ?? 65} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "scale"], Number(e.target.value)))} data-testid="online-sec-scale" />
            </div>
            <div>
              <Label className="text-[10px]">Margem superior ({cfg.online_secondary_section?.margin_top ?? 16}px)</Label>
              <Input type="range" min={0} max={80} value={cfg.online_secondary_section?.margin_top ?? 16} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "margin_top"], Number(e.target.value)))} data-testid="online-sec-mt" />
            </div>
            <div>
              <Label className="text-[10px]">Espaço entre cards ({cfg.online_secondary_section?.card_gap ?? 10}px)</Label>
              <Input type="range" min={0} max={40} value={cfg.online_secondary_section?.card_gap ?? 10} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "card_gap"], Number(e.target.value)))} data-testid="online-sec-gap" />
            </div>
            <div>
              <Label className="text-[10px]">Raio cantos ({cfg.online_secondary_section?.container_radius ?? 22}px)</Label>
              <Input type="range" min={0} max={40} value={cfg.online_secondary_section?.container_radius ?? 22} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "container_radius"], Number(e.target.value)))} data-testid="online-sec-radius" />
            </div>
            <ColorField label="Fundo" value={cfg.online_secondary_section?.container_bg || "#050D2E"} onChange={(v) => setCfg(setPath(cfg, ["online_secondary_section", "container_bg"], v))} testid="online-sec-bg" />
            <ColorField label="Rebordo" value={cfg.online_secondary_section?.container_border || "#3DA5FF"} onChange={(v) => setCfg(setPath(cfg, ["online_secondary_section", "container_border"], v))} testid="online-sec-border" />
          </div>

          {/* ── Botão JOGAR — tamanho (igual ao bloco principal, mas escalado) ── */}
          <div className="border-t border-lime-200 pt-2 mt-1">
            <div className="text-[11px] font-bold text-lime-800 mb-1">Botão JOGAR — tamanho</div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px]">Fonte ({cfg.online_secondary_section?.card_button_size ?? 16}px)</Label>
                <Input type="range" min={10} max={28} value={cfg.online_secondary_section?.card_button_size ?? 16} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "card_button_size"], Number(e.target.value)))} data-testid="online-sec-btn-font" />
              </div>
              <div>
                <Label className="text-[10px]">Altura ({cfg.online_secondary_section?.card_button_h ?? 42}px)</Label>
                <Input type="range" min={24} max={80} value={cfg.online_secondary_section?.card_button_h ?? 42} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "card_button_h"], Number(e.target.value)))} data-testid="online-sec-btn-h" />
              </div>
              <div>
                <Label className="text-[10px]">Largura ({cfg.online_secondary_section?.card_button_w ?? 140}px, 0=100%)</Label>
                <Input type="range" min={0} max={260} value={cfg.online_secondary_section?.card_button_w ?? 140} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "card_button_w"], Number(e.target.value)))} data-testid="online-sec-btn-w" />
              </div>
            </div>
            <p className="text-[10px] text-slate-500 italic mt-1">Valores são multiplicados pela escala global do bloco secundário.</p>
          </div>

          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={cfg.online_secondary_section?.show_descriptions !== false}
              onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "show_descriptions"], e.target.checked))}
              data-testid="online-sec-showdesc"
            />
            <span>Mostrar descrições dentro dos cards</span>
          </label>

          <label className="flex items-center gap-2 text-xs cursor-pointer border-t border-lime-200 pt-2">
            <input
              type="checkbox"
              checked={cfg.online_secondary_section?.inherit_cards !== false}
              onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "inherit_cards"], e.target.checked))}
              data-testid="online-sec-inherit"
            />
            <span className="font-bold">Herdar os cards do bloco principal (automático)</span>
          </label>

          {/* Independent cards editor (só quando NOT inherit) */}
          {cfg.online_secondary_section?.inherit_cards === false && (
            <div className="space-y-2 pt-2 border-t border-lime-200">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-bold text-lime-800">Cards independentes</div>
                <button
                  type="button"
                  onClick={() => {
                    const cards = cfg.online_secondary_section?.cards || [];
                    const newCard = { id: `card_${Date.now()}`, icon: "Clock", color: "blue", url: "/", icon_url: "", emoji_override: "",
                                       title_pt: "NOVO", title_en: "NEW", title_es: "NUEVO",
                                       description_pt: "…", description_en: "…", description_es: "…",
                                       button_pt: "JOGAR", button_en: "PLAY", button_es: "JUGAR" };
                    setCfg(setPath(cfg, ["online_secondary_section", "cards"], [...cards, newCard]));
                  }}
                  className="px-2 py-1 rounded-md bg-lime-500 hover:bg-lime-600 text-white text-[10px] font-bold flex items-center gap-1"
                  data-testid="online-sec-add-card"
                ><Plus className="w-3 h-3" /> Adicionar card</button>
              </div>
              {(cfg.online_secondary_section?.cards || []).map((c, idx) => (
                <div key={c.id || `sec-${idx}`} className="rounded border border-slate-200 p-2 bg-white space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-bold text-slate-600">Card {idx + 1}</div>
                    <button
                      type="button"
                      onClick={() => {
                        const arr = [...(cfg.online_secondary_section?.cards || [])];
                        arr.splice(idx, 1);
                        setCfg(setPath(cfg, ["online_secondary_section", "cards"], arr));
                      }}
                      className="text-rose-600 text-[10px] hover:underline"
                      data-testid={`online-sec-remove-${idx}`}
                    >Remover</button>
                  </div>
                  <div className="grid grid-cols-3 gap-1">
                    <Input value={c.title_pt || ""} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "cards", idx, "title_pt"], e.target.value))} placeholder="Título PT" data-testid={`online-sec-${idx}-title`} className="h-7 text-xs" />
                    <Input value={c.url || ""} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "cards", idx, "url"], e.target.value))} placeholder="/url" className="h-7 text-xs" />
                    <Input value={c.icon || ""} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "cards", idx, "icon"], e.target.value))} placeholder="Clock/Bot/Users" className="h-7 text-xs" />
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <Input value={c.emoji_override || ""} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "cards", idx, "emoji_override"], e.target.value))} placeholder="emoji (⏱️)" className="h-7 text-xs" />
                    <Input value={c.icon_url || ""} onChange={(e) => setCfg(setPath(cfg, ["online_secondary_section", "cards", idx, "icon_url"], e.target.value))} placeholder="URL imagem" className="h-7 text-xs" />
                  </div>
                </div>
              ))}
              {(cfg.online_secondary_section?.cards || []).length === 0 && (
                <div className="text-[10px] text-slate-500 italic p-2">Ainda sem cards. Adiciona um acima ou reativa &quot;Herdar cards&quot;.</div>
              )}
            </div>
          )}
        </div>
        <hr className="my-3" />
        <LearnLinkButtonPanel cfg={cfg} setCfg={setCfg} />
      </Section>

      {/* FOOTER STATS */}
      <Section id="footer" icon={BarChart3} title="Rodapé com estatísticas + redes sociais" open={open.footer} onToggle={() => toggleOpen("footer")}>
        {/* ── Posicionamento vertical na página (puxa para cima / fim do site) ── */}
        <div className="rounded-lg border-2 border-lime-300 bg-lime-50 p-3 space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-wide text-lime-700">Posição vertical & fim do site</div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] text-slate-600">Folga extra abaixo do último bloco ({cfg.footer_stats?.section_margin_top ?? 0}px)</Label>
              <input
                type="range" min={0} max={80} step={2}
                value={cfg.footer_stats?.section_margin_top ?? 0}
                onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "section_margin_top"], Number(e.target.value)))}
                className="w-full mt-1"
                data-testid="footer-section-mt"
              />
            </div>
            <div>
              <Label className="text-[10px] text-slate-600">Folga acima ({cfg.footer_stats?.section_padding_top ?? 8}px)</Label>
              <input
                type="range" min={0} max={80}
                value={cfg.footer_stats?.section_padding_top ?? 8}
                onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "section_padding_top"], Number(e.target.value)))}
                className="w-full mt-1"
                data-testid="footer-section-pt"
              />
            </div>
            <div>
              <Label className="text-[10px] text-slate-600">Fim do site ({cfg.footer_stats?.section_padding_bottom ?? 8}px)</Label>
              <input
                type="range" min={0} max={120}
                value={cfg.footer_stats?.section_padding_bottom ?? 8}
                onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "section_padding_bottom"], Number(e.target.value)))}
                className="w-full mt-1"
                data-testid="footer-section-pb"
              />
            </div>
          </div>
          <p className="text-[10px] text-lime-800/80 italic">
            A FAIXA orienta-se automaticamente pelo &quot;Bloco Vídeo&quot; (ou &quot;Joga Online&quot; se aquele estiver desactivado) e nunca se sobrepõe a nenhum outro bloco.
            &quot;Folga extra&quot; adiciona apenas espaço vertical entre o último bloco e a faixa (nunca subtrai).
          </p>
        </div>

        {/* ── Stat items (4 blocks) ─────────────────────────────────────── */}
        <div>
          <Label className="text-sm font-bold text-slate-700 mb-2 block">Blocos de estatísticas (ícone + valor + etiqueta)</Label>
          <div className="grid sm:grid-cols-2 gap-3">
            {(cfg.footer_stats?.items || []).map((s, idx) => (
              <div key={s.id || `stat-${idx}`} className={`rounded-lg border p-3 space-y-2 ${s.enabled === false ? "bg-slate-100 border-slate-200 opacity-60" : "bg-slate-50 border-slate-200"}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Bloco {idx + 1} · {s.id}</div>
                  <label className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={s.enabled !== false}
                      onChange={(e) => updateFooterStatItem(idx, "enabled", e.target.checked)}
                      className="accent-emerald-600"
                      data-testid={`stat-${idx}-enabled`}
                    />
                    <span className={`font-bold ${s.enabled === false ? "text-slate-400" : "text-emerald-700"}`}>
                      {s.enabled === false ? "Não activo" : "Activo"}
                    </span>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={s.value || ""} onChange={(e) => updateFooterStatItem(idx, "value", e.target.value)} placeholder="ex: 25+" data-testid={`stat-${idx}-value`} />
                  <Input value={s.emoji || ""} onChange={(e) => updateFooterStatItem(idx, "emoji", e.target.value)} placeholder="🌐 (emoji)" data-testid={`stat-${idx}-emoji`} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] text-slate-500">Cor de fundo</Label>
                    <Input type="color" value={s.bg_color || "#1B6FD1"} onChange={(e) => updateFooterStatItem(idx, "bg_color", e.target.value)} className="h-8 w-full" data-testid={`stat-${idx}-bg`} />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">Cor do glow</Label>
                    <Input type="color" value={s.glow_color || "#3DA5FF"} onChange={(e) => updateFooterStatItem(idx, "glow_color", e.target.value)} className="h-8 w-full" data-testid={`stat-${idx}-glow`} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-2">
                  {LANGS.map(({ code, flag }) => (
                    <Input
                      key={code}
                      value={s[`label_${code}`] || ""}
                      onChange={(e) => updateFooterStatItem(idx, `label_${code}`, e.target.value)}
                      placeholder={`${flag} Etiqueta`}
                      data-testid={`stat-${idx}-label-${code}`}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Container styling (background, border, glow, stripes) ────── */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Contentor da faixa</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-slate-500">Fundo — cor de cima</Label>
              <Input type="color" value={cfg.footer_stats.container_bg_from || "#02071E"} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "container_bg_from"], e.target.value))} className="h-8 w-full" data-testid="footer-bg-from" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Fundo — cor de baixo</Label>
              <Input type="color" value={cfg.footer_stats.container_bg_to || "#071B52"} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "container_bg_to"], e.target.value))} className="h-8 w-full" data-testid="footer-bg-to" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Rebordo (cor)</Label>
              <Input type="color" value={cfg.footer_stats.container_border_color || "#3DA5FF"} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "container_border_color"], e.target.value))} className="h-8 w-full" data-testid="footer-border-color" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Rebordo (opacidade {cfg.footer_stats.container_border_opacity ?? 45}%)</Label>
              <input type="range" min={0} max={100} step={5} value={cfg.footer_stats.container_border_opacity ?? 45} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "container_border_opacity"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-border-opacity" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Largura máxima ({cfg.footer_stats.container_max_width ?? 1500}px)</Label>
              <input type="range" min={800} max={1920} step={20} value={cfg.footer_stats.container_max_width ?? 1500} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "container_max_width"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-max-width" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Cantos arredondados ({cfg.footer_stats.container_border_radius ?? 22}px)</Label>
              <input type="range" min={0} max={60} value={cfg.footer_stats.container_border_radius ?? 22} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "container_border_radius"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-border-radius" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Altura mínima ({cfg.footer_stats.container_min_height ?? 128}px)</Label>
              <input type="range" min={80} max={220} value={cfg.footer_stats.container_min_height ?? 128} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "container_min_height"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-min-height" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Padding X ({cfg.footer_stats.container_padding_x ?? 22}px)</Label>
              <input type="range" min={0} max={60} value={cfg.footer_stats.container_padding_x ?? 22} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "container_padding_x"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-padx" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Padding Y ({cfg.footer_stats.container_padding_y ?? 16}px)</Label>
              <input type="range" min={0} max={40} value={cfg.footer_stats.container_padding_y ?? 16} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "container_padding_y"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-pady" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Glow (cor)</Label>
              <Input type="color" value={cfg.footer_stats.container_glow_color || "#3DA5FF"} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "container_glow_color"], e.target.value))} className="h-8 w-full" data-testid="footer-glow-color" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Glow (intensidade {cfg.footer_stats.container_glow_intensity ?? 18})</Label>
              <input type="range" min={0} max={60} value={cfg.footer_stats.container_glow_intensity ?? 18} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "container_glow_intensity"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-glow-intensity" />
            </div>
            <div className="col-span-2 flex items-center gap-3 pt-1">
              <label className="text-xs text-slate-600 flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={cfg.footer_stats.show_purple_stripes !== false} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "show_purple_stripes"], e.target.checked))} data-testid="footer-stripes-on" />
                Mostrar riscas roxas (esquerda)
              </label>
              <Input type="color" value={cfg.footer_stats.purple_stripes_color || "#9B4DFF"} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "purple_stripes_color"], e.target.value))} className="h-8 w-16" data-testid="footer-stripes-color" />
            </div>
          </div>
        </div>

        {/* ── Stat block typography + separators ───────────────────────── */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Tipografia dos blocos</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-slate-500">Tamanho do ícone ({cfg.footer_stats.stat_icon_size ?? 72}px)</Label>
              <input type="range" min={40} max={120} value={cfg.footer_stats.stat_icon_size ?? 72} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "stat_icon_size"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-icon-size" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Espaço ícone↔texto ({cfg.footer_stats.stat_gap ?? 12}px)</Label>
              <input type="range" min={0} max={40} value={cfg.footer_stats.stat_gap ?? 12} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "stat_gap"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-stat-gap" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Tamanho do valor ({cfg.footer_stats.stat_value_size ?? 30}px)</Label>
              <input type="range" min={16} max={60} value={cfg.footer_stats.stat_value_size ?? 30} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "stat_value_size"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-val-size" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Cor do valor</Label>
              <Input type="color" value={cfg.footer_stats.stat_value_color || "#FFFFFF"} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "stat_value_color"], e.target.value))} className="h-8 w-full" data-testid="footer-val-color" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Tamanho da etiqueta ({cfg.footer_stats.stat_label_size ?? 13}px)</Label>
              <input type="range" min={8} max={24} value={cfg.footer_stats.stat_label_size ?? 13} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "stat_label_size"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-label-size" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Cor da etiqueta</Label>
              <Input type="color" value={cfg.footer_stats.stat_label_color || "#FFFFFF"} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "stat_label_color"], e.target.value))} className="h-8 w-full" data-testid="footer-label-color" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] text-slate-500">Opacidade da etiqueta ({cfg.footer_stats.stat_label_opacity ?? 85}%)</Label>
              <input type="range" min={0} max={100} value={cfg.footer_stats.stat_label_opacity ?? 85} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "stat_label_opacity"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-label-opacity" />
            </div>
            <div className="col-span-2 text-[10px] font-bold uppercase tracking-wide text-slate-500 mt-2">Separadores verticais</div>
            <div>
              <Label className="text-[10px] text-slate-500">Cor</Label>
              <Input type="color" value={cfg.footer_stats.separator_color || "#3DA5FF"} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "separator_color"], e.target.value))} className="h-8 w-full" data-testid="footer-sep-color" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Opacidade ({cfg.footer_stats.separator_opacity ?? 30}%)</Label>
              <input type="range" min={0} max={100} value={cfg.footer_stats.separator_opacity ?? 30} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "separator_opacity"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-sep-opacity" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] text-slate-500">Altura ({cfg.footer_stats.separator_height ?? 76}px)</Label>
              <input type="range" min={20} max={140} value={cfg.footer_stats.separator_height ?? 76} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "separator_height"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-sep-height" />
            </div>
          </div>
        </div>

        {/* ── Central pill (tagline container) ─────────────────────────── */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Pílula central (tagline)</div>
          <MultiLangText
            label="Tagline — 1.ª linha (branca)"
            multiline
            testidPrefix="footer-tagline"
            value={{ pt: cfg.footer_stats.tagline_pt, en: cfg.footer_stats.tagline_en, es: cfg.footer_stats.tagline_es }}
            onChange={updateFooterTagline}
          />
          <MultiLangText
            label="Tagline — 2.ª linha destacada (amarela)"
            testidPrefix="footer-tagline-highlight"
            value={{
              pt: cfg.footer_stats.tagline_highlight_pt || "",
              en: cfg.footer_stats.tagline_highlight_en || "",
              es: cfg.footer_stats.tagline_highlight_es || "",
            }}
            onChange={updateFooterTaglineHighlight}
          />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-slate-500">Cor 1.ª linha</Label>
              <Input type="color" value={cfg.footer_stats.tagline_color || "#FFFFFF"} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "tagline_color"], e.target.value))} className="h-8 w-full" data-testid="footer-tag-color" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Cor 2.ª linha (destaque)</Label>
              <Input type="color" value={cfg.footer_stats.tagline_highlight_color || "#FFD400"} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "tagline_highlight_color"], e.target.value))} className="h-8 w-full" data-testid="footer-tagline-highlight-color" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Tamanho 1.ª linha ({cfg.footer_stats.tagline_size ?? 15}px)</Label>
              <input type="range" min={8} max={30} value={cfg.footer_stats.tagline_size ?? 15} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "tagline_size"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-tag-size" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Tamanho 2.ª linha ({cfg.footer_stats.tagline_highlight_size ?? 20}px)</Label>
              <input type="range" min={10} max={40} value={cfg.footer_stats.tagline_highlight_size ?? 20} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "tagline_highlight_size"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-tag-highlight-size" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Cor de fundo (pílula)</Label>
              <Input type="color" value={cfg.footer_stats.pill_bg_color || "#02071E"} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "pill_bg_color"], e.target.value))} className="h-8 w-full" data-testid="footer-pill-bg" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Opacidade fundo ({cfg.footer_stats.pill_bg_opacity ?? 60}%)</Label>
              <input type="range" min={0} max={100} value={cfg.footer_stats.pill_bg_opacity ?? 60} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "pill_bg_opacity"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-pill-bg-opacity" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Cor rebordo</Label>
              <Input type="color" value={cfg.footer_stats.pill_border_color || "#3DA5FF"} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "pill_border_color"], e.target.value))} className="h-8 w-full" data-testid="footer-pill-border-color" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Opacidade rebordo ({cfg.footer_stats.pill_border_opacity ?? 55}%)</Label>
              <input type="range" min={0} max={100} value={cfg.footer_stats.pill_border_opacity ?? 55} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "pill_border_opacity"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-pill-border-opacity" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Raio cantos ({cfg.footer_stats.pill_border_radius ?? 46}px)</Label>
              <input type="range" min={0} max={80} value={cfg.footer_stats.pill_border_radius ?? 46} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "pill_border_radius"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-pill-radius" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Altura mínima ({cfg.footer_stats.pill_min_height ?? 92}px)</Label>
              <input type="range" min={40} max={160} value={cfg.footer_stats.pill_min_height ?? 92} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "pill_min_height"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-pill-min-h" />
            </div>
          </div>
        </div>

        {/* ── Social icons (URLs + visibility + sizing) ────────────────── */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Redes sociais</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-slate-500">Tamanho ({cfg.footer_stats.social_size ?? 60}px)</Label>
              <input type="range" min={32} max={100} value={cfg.footer_stats.social_size ?? 60} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "social_size"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-social-size" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Espaço entre ({cfg.footer_stats.social_gap ?? 10}px)</Label>
              <input type="range" min={0} max={30} value={cfg.footer_stats.social_gap ?? 10} onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "social_gap"], Number(e.target.value)))} className="w-full mt-1" data-testid="footer-social-gap" />
            </div>
          </div>
          {["youtube", "instagram", "facebook", "tiktok"].map((k) => (
            <div key={k} className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-xs text-slate-700 w-28 flex-shrink-0 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cfg.footer_stats[`social_show_${k}`] !== false}
                  onChange={(e) => setCfg(setPath(cfg, ["footer_stats", `social_show_${k}`], e.target.checked))}
                  data-testid={`footer-social-show-${k}`}
                />
                <span className="capitalize font-bold">{k}</span>
              </label>
              <Input
                value={cfg.footer_stats.social_links?.[k] || ""}
                onChange={(e) => updateSocial(k, e.target.value)}
                placeholder={`${k} URL (# para placeholder)`}
                data-testid={`social-${k}`}
                className="flex-1"
              />
            </div>
          ))}
        </div>

        {/* ── Legal strip (Termos · Privacidade · Reclamações · Copyright) ── */}
        <div className="border-t-2 border-slate-200 pt-4 mt-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] font-black uppercase tracking-wide text-slate-700">⚖️ Linha legal (tudo numa linha, abaixo da FAIXA)</div>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={cfg.footer_stats?.legal_show !== false}
                onChange={(e) => setCfg(setPath(cfg, ["footer_stats", "legal_show"], e.target.checked))}
                data-testid="legal-show"
              />
              <span className="font-bold">Mostrar</span>
            </label>
          </div>

          {[
            { key: "terms",      label: "Termos e Condições",       urlKey: "legal_terms_url",      urlPh: "/termos"                        },
            { key: "privacy",    label: "Política de Privacidade",  urlKey: "legal_privacy_url",    urlPh: "/privacidade"                   },
            { key: "complaints", label: "Livro de Reclamações",     urlKey: "legal_complaints_url", urlPh: "https://livroreclamacoes.pt/…" },
          ].map(({ key, label, urlKey, urlPh }) => (
            <div key={key} className="rounded border border-slate-200 bg-slate-50 p-2 space-y-1.5">
              <div className="text-[11px] font-bold text-slate-700">{label}</div>
              <MultiLangText
                label=""
                testidPrefix={`legal-${key}`}
                value={{
                  pt: cfg.footer_stats?.[`legal_${key}_pt`] || "",
                  en: cfg.footer_stats?.[`legal_${key}_en`] || "",
                  es: cfg.footer_stats?.[`legal_${key}_es`] || "",
                }}
                onChange={(code, v) => setCfg(setPath(cfg, ["footer_stats", `legal_${key}_${code}`], v))}
              />
              <div>
                <Label className="text-[10px] text-slate-500">URL (comum aos 3 idiomas)</Label>
                <Input
                  value={cfg.footer_stats?.[urlKey] || ""}
                  onChange={(e) => setCfg(setPath(cfg, ["footer_stats", urlKey], e.target.value))}
                  placeholder={urlPh}
                  className="h-8 text-xs"
                  data-testid={`legal-${key}-url`}
                />
              </div>
            </div>
          ))}

          <div className="rounded border border-slate-200 bg-slate-50 p-2 space-y-1.5">
            <div className="text-[11px] font-bold text-slate-700">Copyright</div>
            <MultiLangText
              label=""
              testidPrefix="legal-copyright"
              value={{
                pt: cfg.footer_stats?.legal_copyright_pt || "",
                en: cfg.footer_stats?.legal_copyright_en || "",
                es: cfg.footer_stats?.legal_copyright_es || "",
              }}
              onChange={(code, v) => setCfg(setPath(cfg, ["footer_stats", `legal_copyright_${code}`], v))}
            />
          </div>
          <p className="text-[10px] text-slate-500 italic">Os 4 elementos aparecem <b>na mesma linha</b> abaixo da FAIXA (com wrap em ecrãs muito estreitos). Deixa um texto vazio para o esconder.</p>
        </div>
      </Section>

      {/* EMAIL / SMTP — configuração do servidor de envio de credenciais */}
      <Section id="smtp" icon={Mail} title="Email (SMTP) — Envio de credenciais" open={open.smtp} onToggle={() => toggleOpen("smtp")}>
        <SmtpPanel />
      </Section>
    </div>
  );
}
